import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useAITasks } from '../contexts/AITaskContext';
import { 
  Sparkles, Music, Scissors, Mic, Wand2, Loader2, AlertCircle,
  Play, Pause, Download, Upload, FolderOpen, Check, X, Trash2,
  RefreshCw, ChevronDown, ChevronRight, Volume2, VolumeX, Maximize2, Clock, RotateCcw,
  Guitar, MicVocal, Replace, Shuffle, FileAudio, FileText, Zap, UserCircle,
  AudioWaveform, ImagePlus, FastForward, Copy, FolderPlus, XCircle, Menu, PanelLeftClose
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import {
  ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuItem, ContextMenuSeparator,
} from '../components/ui/context-menu';
import { toast } from 'sonner';
import { playTrack, useGlobalPlayer, togglePlay } from '../components/GlobalMusicPlayer';
import AnimatedEqualizer from '../components/AnimatedEqualizer';
import SongContextMenu from '../components/SongContextMenu';
import { downloadFile } from '../utils/downloadFile';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================
// COMPONENTS DEFINED OUTSIDE - CRITICAL!
// ============================================

const GlassCard = ({ children, className = '', style = {} }) => (
  <div 
    className={`backdrop-blur-xl rounded-xl p-6 ${className}`}
    style={{ 
      backgroundColor: 'var(--bg-card)',
      border: '1px solid var(--border-color)',
      ...style
    }}
  >
    {children}
  </div>
);

const StudioTextarea = ({ placeholder, defaultValue = '', inputRef, onInput, className = '', ...props }) => (
  <textarea
    ref={inputRef}
    defaultValue={defaultValue}
    onInput={onInput}
    placeholder={placeholder}
    className={`flex w-full rounded-md border px-3 py-2 text-base placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 ${className}`}
    style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
    autoComplete="off"
    spellCheck="false"
    {...props}
  />
);

const StudioInput = ({ placeholder, defaultValue = '', inputRef, type = 'text', className = '', ...props }) => (
  <input
    ref={inputRef}
    type={type}
    defaultValue={defaultValue}
    placeholder={placeholder}
    className={`flex h-10 w-full rounded-md border px-3 py-2 text-base placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 ${className}`}
    style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
    autoComplete="off"
    spellCheck="false"
    {...props}
  />
);

// ============================================
// MAIN COMPONENT
// ============================================

const AIStudio = () => {
  const { theme, isDarkMode } = useTheme();
  const { token, isAdmin, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('generate');
  const [aiConfig, setAiConfig] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState({});
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const toggleCategory = (cat) => setSidebarCollapsed(prev => ({ ...prev, [cat]: !prev[cat] }));
  const [sidebarWidth, setSidebarWidth] = useState(224);
  const [outputWidth, setOutputWidth] = useState(320);
  const isDraggingRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const handleResizeStart = useCallback((panel, e) => {
    e.preventDefault();
    isDraggingRef.current = panel;
    startXRef.current = e.clientX;
    startWidthRef.current = panel === 'sidebar' ? sidebarWidth : outputWidth;

    const handleMouseMove = (ev) => {
      const delta = ev.clientX - startXRef.current;
      if (isDraggingRef.current === 'sidebar') {
        setSidebarWidth(Math.max(180, Math.min(400, startWidthRef.current + delta)));
      } else if (isDraggingRef.current === 'output') {
        setOutputWidth(Math.max(240, Math.min(600, startWidthRef.current - delta)));
      }
    };
    const handleMouseUp = () => {
      isDraggingRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [sidebarWidth, outputWidth]);
  
  // Global player state for tracking current playing song
  const { currentTrack, isPlaying } = useGlobalPlayer();
  
  // Helper to check if a song is currently playing
  const isCurrentlyPlaying = (song) => {
    if (!currentTrack || !isPlaying) return false;
    
    // Get normalized audio URLs for comparison
    const getSongUrl = (url) => {
      if (!url) return null;
      if (typeof url === 'object') {
        return url.instrumental || url.vocals || Object.values(url)[0];
      }
      if (typeof url === 'string' && url.startsWith('{')) {
        try {
          const parsed = JSON.parse(url);
          return parsed.instrumental || parsed.vocals || Object.values(parsed)[0];
        } catch (e) {
          return url;
        }
      }
      return url;
    };
    
    const songUrl = getSongUrl(song.audio_url || song.file_url);
    const currentUrl = getSongUrl(currentTrack.audio_url || currentTrack.url);
    
    // Primary match: by audio URL (most reliable)
    if (songUrl && currentUrl && songUrl === currentUrl) return true;
    
    // Fallback: match by unique ID if available
    if (song.id && currentTrack.id && song.id === currentTrack.id) return true;
    
    return false;
  };
  
  // Song libraries
  const [musicLibrary, setMusicLibrary] = useState([]);
  const [aiLibrary, setAiLibrary] = useState([]);
  const [trashItems, setTrashItems] = useState([]);
  const [showSongPicker, setShowSongPicker] = useState(false);
  const [songPickerTarget, setSongPickerTarget] = useState(null);
  const { tasks, activeTasks: globalActiveTasks, refreshTasks } = useAITasks();

  // Generation state - using refs for input values to prevent re-renders stealing focus
  // State is only used for submission, refs hold the actual typing values
  const genPromptValueRef = useRef('');
  const genLyricsValueRef = useRef('');
  const [genStyle, setGenStyle] = useState('Hip-Hop');
  // genStyle is kept for backward compat but actual style comes from genre system
  const [customStyleTags, setCustomStyleTags] = useState('');
  const [isInstrumental, setIsInstrumental] = useState(false);
  const [isExplicit, setIsExplicit] = useState(false);
  const [vocalType, setVocalType] = useState('auto');
  const [modelVersion, setModelVersion] = useState('V4');
  const [generatingLyrics, setGeneratingLyrics] = useState(false);
  const [promptCharCount, setPromptCharCount] = useState(0);
  const [genProvider, setGenProvider] = useState('suno'); // 'musicgpt' or 'suno'
  const [outputLength, setOutputLength] = useState('auto');
  const [extendUrl, setExtendUrl] = useState('');
  const [extendAudioId, setExtendAudioId] = useState('');
  const [extendTaskId, setExtendTaskId] = useState('');
  const [extendSongTitle, setExtendSongTitle] = useState('');
  const [extendLoading, setExtendLoading] = useState(false);
  const [extendContinueAt, setExtendContinueAt] = useState('60');
  const [extendPrompt, setExtendPrompt] = useState('');
  const [extendStyle, setExtendStyle] = useState('Hip-Hop, Trap');
  const [extendTitle, setExtendTitle] = useState('');
  const [extendModelVersion, setExtendModelVersion] = useState('V4');
  const [extendVocalGender, setExtendVocalGender] = useState('auto');
  const [extendUseOriginal, setExtendUseOriginal] = useState(false);
  const [extendInstrumental, setExtendInstrumental] = useState(false);
  const [extendNegativeTags, setExtendNegativeTags] = useState('');

  // Add Instrumental state
  const [addInstrumentalUrl, setAddInstrumentalUrl] = useState('');
  const [addInstrumentalFileName, setAddInstrumentalFileName] = useState('');
  const [addInstrumentalTitle, setAddInstrumentalTitle] = useState('');
  const [addInstrumentalStyle, setAddInstrumentalStyle] = useState('');
  const [addInstrumentalModel, setAddInstrumentalModel] = useState('V4');
  const [addInstrumentalLoading, setAddInstrumentalLoading] = useState(false);

  // Add Vocals state
  const [addVocalsUrl, setAddVocalsUrl] = useState('');
  const [addVocalsFileName, setAddVocalsFileName] = useState('');
  const [addVocalsPrompt, setAddVocalsPrompt] = useState('');
  const [addVocalsStyle, setAddVocalsStyle] = useState('');
  const [addVocalsGender, setAddVocalsGender] = useState('m');
  const [addVocalsModel, setAddVocalsModel] = useState('V4');
  const [addVocalsLoading, setAddVocalsLoading] = useState(false);

  // Replace Section state
  const [replaceSectionAudioId, setReplaceSectionAudioId] = useState('');
  const [replaceSectionTaskId, setReplaceSectionTaskId] = useState('');
  const [replaceSectionTitle, setReplaceSectionTitle] = useState('');
  const [replaceSectionPrompt, setReplaceSectionPrompt] = useState('');
  const [replaceSectionStyle, setReplaceSectionStyle] = useState('');
  const [replaceSectionStart, setReplaceSectionStart] = useState('0');
  const [replaceSectionEnd, setReplaceSectionEnd] = useState('30');
  const [replaceSectionModel, setReplaceSectionModel] = useState('V4');
  const [replaceSectionLoading, setReplaceSectionLoading] = useState(false);

  // Mashup state
  const [mashupTracks, setMashupTracks] = useState([]);
  const [mashupPrompt, setMashupPrompt] = useState('');
  const [mashupStyle, setMashupStyle] = useState('');
  const [mashupTitle, setMashupTitle] = useState('');
  const [mashupModel, setMashupModel] = useState('V4');
  const [mashupInstrumental, setMashupInstrumental] = useState(true);
  const [mashupCustomMode, setMashupCustomMode] = useState(true);
  const [mashupVocalGender, setMashupVocalGender] = useState('');
  const [mashupStyleWeight, setMashupStyleWeight] = useState(0.5);
  const [mashupWeirdness, setMashupWeirdness] = useState(0.5);
  const [mashupAudioWeight, setMashupAudioWeight] = useState(0.5);
  const [mashupLoading, setMashupLoading] = useState(false);

  // WAV Convert state
  const [wavTaskId, setWavTaskId] = useState('');
  const [wavAudioId, setWavAudioId] = useState('');
  const [coverImageTaskId, setCoverImageTaskId] = useState('');
  const [wavLoading, setWavLoading] = useState(false);
  const [wavResult, setWavResult] = useState(null);

  // Suno Lyrics state
  const [sunoLyricsPrompt, setSunoLyricsPrompt] = useState('');
  const [sunoLyricsLoading, setSunoLyricsLoading] = useState(false);
  const [sunoLyricsResult, setSunoLyricsResult] = useState('');
  const [sunoLyricsVariations, setSunoLyricsVariations] = useState([]);
  const [sunoLyricsVariationIndex, setSunoLyricsVariationIndex] = useState(0);

  // Boost Style state
  const [boostStyleContent, setBoostStyleContent] = useState('');
  const [boostStyleLoading, setBoostStyleLoading] = useState(false);
  const [boostStyleResult, setBoostStyleResult] = useState('');

  // Persona state
  const [personaTaskId, setPersonaTaskId] = useState('');
  const [personaAudioId, setPersonaAudioId] = useState('');
  const [personaName, setPersonaName] = useState('');
  const [personaDescription, setPersonaDescription] = useState('');
  const [personaVocalStart, setPersonaVocalStart] = useState(0);
  const [personaVocalEnd, setPersonaVocalEnd] = useState(30);
  const [personaStyle, setPersonaStyle] = useState('');
  const [personaLoading, setPersonaLoading] = useState(false);
  const [personaResult, setPersonaResult] = useState('');

  // New features state
  const [isGenerating, setIsGenerating] = useState(false);
  const [coverImageResult, setCoverImageResult] = useState(null);

  // Magic Lyrics Dialog state - using uncontrolled input to avoid focus issues
  const [showLyricsDialog, setShowLyricsDialog] = useState(false);
  const [lyricsDialogTarget, setLyricsDialogTarget] = useState('generate'); // 'generate' or 'cover'
  const lyricsDescRef = useRef(null);
  
  const [activeTasks, setActiveTasks] = useState([]);

  // Stems state
  const [stemsUrl, setStemsUrl] = useState('');
  const [stemsFileName, setStemsFileName] = useState('');
  const [stemType, setStemType] = useState('separate_vocal');
  const [stemsTaskId, setStemsTaskId] = useState('');
  const [stemsAudioId, setStemsAudioId] = useState('');

  const coverStyleOptions = [
    { category: 'Hip-Hop / Rap', styles: [
      { id: 'smooth-hiphop', name: 'Smooth Hip-Hop', tags: 'smooth flow, laid-back beat' },
      { id: 'trap', name: 'Hard Trap', tags: 'heavy 808s, hi-hats, dark melody' },
      { id: 'crunk', name: 'Crunk / Gulf Coast', tags: 'crunk energy, heavy bass, chants' },
      { id: 'boom-bap', name: 'Boom Bap', tags: 'classic drums, sample-based, raw' },
      { id: 'melodic-rap', name: 'Melodic Rap', tags: 'autotune, melodic hooks, emotional' },
    ]},
    { category: 'R&B / Soul', styles: [
      { id: 'modern-rnb', name: 'Modern R&B', tags: 'smooth vocals, synth pads, groove' },
      { id: 'classic-soul', name: 'Classic Soul', tags: 'soulful, warm, live instruments' },
    ]},
    { category: 'Pop / Electronic', styles: [
      { id: 'pop', name: 'Pop', tags: 'catchy, upbeat, polished production' },
      { id: 'edm', name: 'EDM / Electronic', tags: 'synths, drops, electronic energy' },
      { id: 'lofi', name: 'Lo-Fi Chill', tags: 'lo-fi, chill, mellow, vinyl crackle' },
    ]},
    { category: 'Other', styles: [
      { id: 'rock', name: 'Rock', tags: 'electric guitar, drums, energy' },
      { id: 'reggae', name: 'Reggae', tags: 'offbeat rhythm, bass, island vibe' },
      { id: 'latin', name: 'Latin', tags: 'reggaeton, latin groove, percussion' },
    ]},
  ];

  const [coverUrl, setCoverUrl] = useState('');
  const [coverFileName, setCoverFileName] = useState('');
  const [coverStylePreset, setCoverStylePreset] = useState('smooth-hiphop');
  const [coverVocalGender, setCoverVocalGender] = useState('m');
  const [coverCustomStyleTags, setCoverCustomStyleTags] = useState('');
  const [coverMakeInstrumental, setCoverMakeInstrumental] = useState(false);
  const [pitch, setPitch] = useState(0);
  const [keepStylePercent, setKeepStylePercent] = useState(80);
  const [similarity, setSimilarity] = useState(70);

  const [coverLyrics] = useState(''); // Kept for backwards compatibility, using refs now
  const [generatingCoverLyrics, setGeneratingCoverLyrics] = useState(false);
  const [coverTitle, setCoverTitle] = useState('');
  const [coverGenre, setCoverGenre] = useState('auto');
  const [coverDuration, setCoverDuration] = useState('auto');
  const [coverModelVersion, setCoverModelVersion] = useState('V4');

  // Mastering state
  const [masterUrl, setMasterUrl] = useState('');
  const [masterFileName, setMasterFileName] = useState('');
  const [referenceUrl] = useState(''); // Kept for backwards compatibility, using refs now
  const [referenceTrackUrl, setReferenceTrackUrl] = useState('');
  const [referenceTrackFileName, setReferenceTrackFileName] = useState('');
  const [showReferencePicker, setShowReferencePicker] = useState(false);
  const [outputFormat, setOutputFormat] = useState('wav');
  const referenceFileInputRef = useRef(null);

  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);
  
  // Refs for uncontrolled inputs - NO STATE UPDATES during typing
  const genPromptRef = useRef(null);
  const genLyricsRef = useRef(null);
  const coverLyricsRef = useRef(null);
  const voiceIdRef = useRef(null);
  const referenceUrlRef = useRef(null);
  const isTypingRef = useRef(false);

  // Focus handler for lyrics description - simple uncontrolled approach
  const getLyricsDescription = () => lyricsDescRef.current?.value || '';
  
  // Get current values from refs (for submission)
  const getGenPrompt = () => genPromptRef.current?.value || '';
  const getGenLyrics = () => genLyricsRef.current?.value || '';
  const getCoverLyrics = () => coverLyricsRef.current?.value || '';
  const getVoiceId = () => voiceIdRef.current?.value || '';
  const getReferenceUrl = () => referenceUrlRef.current?.value || '';

  // Simple handlers - just update char count, no state updates that cause re-render
  const handlePromptInput = useCallback((e) => {
    isTypingRef.current = true;
    genPromptValueRef.current = e.target.value;
    setPromptCharCount(e.target.value.length);
    setTimeout(() => { isTypingRef.current = false; }, 5000);
  }, []);

  const handleLyricsInput = useCallback(() => {
    isTypingRef.current = true;
    genLyricsValueRef.current = genLyricsRef.current?.value || '';
    setTimeout(() => { isTypingRef.current = false; }, 5000);
  }, []);

  // Set lyrics from Magic Lyrics generator
  const setGeneratedLyrics = (lyrics) => {
    if (genLyricsRef.current) {
      genLyricsRef.current.value = lyrics;
      genLyricsValueRef.current = lyrics;
    }
  };

  const headers = { Authorization: `Bearer ${token}` };

  const genreMap = {
    'Pop': ['Synth Pop', 'Dream Pop', 'Indie Pop', 'Hyperpop', 'Bubblegum Pop'],
    'Rock': ['Indie Rock', 'Alternative Rock', 'Classic Rock', 'Psychedelic Rock'],
    'Hip-Hop': ['Trap', 'Boom Bap', 'Drill', 'Cloud Rap', 'West Coast Hip-Hop', 'Crunk', 'Phonk', 'Memphis Rap', 'Southern Hip-Hop', 'Mumble Rap', 'Conscious Hip-Hop', 'Gangsta Rap'],
    'EDM': ['Future Bass', 'Melodic Dubstep', 'Big Room', 'UK Garage'],
    'Electronic': ['Techno', 'House', 'Ambient', 'Synthwave', 'Drum & Bass'],
    'R&B / Soul': ['Neo-Soul', 'Contemporary R&B', 'Funk', 'Motown', 'Quiet Storm'],
    'Country': ['Modern Country', 'Country Pop', 'Bluegrass', 'Outlaw Country'],
    'Metal': ['Heavy Metal', 'Death Metal', 'Metalcore', 'Nu-Metal', 'Black Metal'],
    'Jazz': ['Smooth Jazz', 'Bebop', 'Fusion', 'Nu-Jazz', 'Lo-fi Jazz'],
    'Indie / Alternative': ['Bedroom Pop', 'Shoegaze', 'Grunge', 'Post-Punk', 'Folk Indie'],
    'Latin': ['Reggaeton', 'Salsa', 'Bachata', 'Latin Trap', 'Bossa Nova'],
    'K-Pop / J-Pop': ['City Pop', 'J-Rock', 'K-Hip-Hop', 'Korean R&B'],
    'Folk': ['Indie Folk', 'Celtic Folk', 'Americana', 'Acoustic Folk'],
    'Reggae / Dancehall': ['Roots Reggae', 'Dub', 'Ska', 'Ragga'],
    'Blues': ['Chicago Blues', 'Delta Blues', 'Blues Rock', 'Modern Blues'],
    'Classical': ['Orchestral', 'Piano Solo', 'Cinematic', 'Baroque', 'Minimalist'],
    'Punk': ['Pop Punk', 'Hardcore Punk', 'Emo', 'Skate Punk'],
    'Lo-fi / Chill': ['Lo-fi Hip-Hop', 'Chillhop', 'Vaporwave', 'Chillwave'],
    'Gospel / Christian': ['Contemporary Gospel', 'Worship', 'Southern Gospel'],
    'Experimental': ['Glitch', 'Noise', 'Avant-Garde', 'IDM', 'Microsound'],
    'Afrobeat': ['Afro-Fusion', 'Afro-Pop', 'Amapiano', 'Highlife'],
  };

  const mainGenres = Object.keys(genreMap);
  const [selectedMainGenre, setSelectedMainGenre] = useState('Hip-Hop');
  const [selectedSubGenre, setSelectedSubGenre] = useState('');

  const getEffectiveStyle = () => {
    if (selectedSubGenre) return `${selectedMainGenre}, ${selectedSubGenre}`;
    return selectedMainGenre;
  };

  const handleMainGenreChange = (genre) => {
    setSelectedMainGenre(genre);
    setSelectedSubGenre('');
    setGenStyle(genre);
  };

  const handleSubGenreChange = (sub) => {
    setSelectedSubGenre(sub);
    if (sub) {
      const parent = selectedMainGenre;
      setGenStyle(`${parent}, ${sub}`);
    } else {
      setGenStyle(selectedMainGenre);
    }
  };

  const vocalTypes = [
    { value: 'auto', label: 'Auto (AI Choice)' },
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'male_female', label: 'Male + Female' },
    { value: 'female_male', label: 'Female + Male' },
    { value: 'male_lead_backup', label: 'Male Lead + Backup' },
    { value: 'female_lead_backup', label: 'Female Lead + Backup' },
    { value: 'choir', label: 'Choir' }
  ];

  const modelVersions = [
    { value: 'v3', label: 'MusicGPT v3' },
    { value: 'v3.5', label: 'MusicGPT v3.5 (Recommended)' },
    { value: 'v4', label: 'MusicGPT v4 (Latest)' }
  ];

  const isOwner = user?.role === 'owner';

  useEffect(() => {
    if (isAdmin && token) {
      fetchSongLibraries();
      fetchAiConfig();
      fetchTrash();
    }
  }, [isAdmin, token]);

  const fetchAiConfig = async () => {
    try {
      const res = await axios.get(`${API}/ai/config`);
      setAiConfig(res.data);
    } catch (error) {
      console.error('Error fetching AI config:', error);
    }
  };
  
  useEffect(() => {
    const action = searchParams.get('action');
    const audioUrl = searchParams.get('audioUrl');
    const urlTaskId = searchParams.get('taskId') || '';
    const urlAudioId = searchParams.get('audioId') || '';
    const urlTitle = searchParams.get('title') || 'Selected Track';
    if (action && (audioUrl || urlTaskId || urlAudioId)) {
      const decodedUrl = audioUrl ? decodeURIComponent(audioUrl) : '';
      if (action === 'stems') {
        setActiveTab('stems');
        setStemsUrl(decodedUrl);
        setStemsFileName(urlTitle);
        if (urlTaskId) setStemsTaskId(urlTaskId);
        if (urlAudioId) setStemsAudioId(urlAudioId);
      } else if (action === 'cover') {
        setActiveTab('cover');
        setCoverUrl(decodedUrl);
        setCoverFileName(urlTitle);
      } else if (action === 'master') {
        setActiveTab('master');
        setMasterUrl(decodedUrl);
        setMasterFileName(urlTitle);
      } else if (action === 'extend') {
        setActiveTab('extend');
        setExtendUrl(decodedUrl);
        setExtendSongTitle(urlTitle);
        if (urlTaskId) setExtendTaskId(urlTaskId);
        if (urlAudioId) setExtendAudioId(urlAudioId);
      } else if (action === 'add_instrumental') {
        setActiveTab('add_instrumental');
        setAddInstrumentalUrl(decodedUrl);
        setAddInstrumentalFileName(urlTitle);
      } else if (action === 'add_vocals') {
        setActiveTab('add_vocals');
        setAddVocalsUrl(decodedUrl);
        setAddVocalsFileName(urlTitle);
      } else if (action === 'mashup') {
        setActiveTab('mashup');
        setMashupTracks([{ url: decodedUrl, name: urlTitle }]);
      } else if (action === 'wav_convert') {
        setActiveTab('wav_convert');
        if (urlTaskId) setWavTaskId(urlTaskId);
        if (urlAudioId) setWavAudioId(urlAudioId);
      } else if (action === 'replace_section') {
        setActiveTab('replace_section');
        setReplaceSectionTitle(urlTitle);
        if (urlTaskId) setReplaceSectionTaskId(urlTaskId);
        if (urlAudioId) setReplaceSectionAudioId(urlAudioId);
      } else if (action === 'persona') {
        setActiveTab('persona');
        if (urlTaskId) setPersonaTaskId(urlTaskId);
        if (urlAudioId) setPersonaAudioId(urlAudioId);
      } else if (action === 'cover_image') {
        setActiveTab('cover_image');
        if (urlTaskId) setCoverImageTaskId(urlTaskId);
      }
      setSearchParams({}, { replace: true });
    }
  }, []);

  useEffect(() => {
    setActiveTasks(globalActiveTasks);
  }, [globalActiveTasks]);

  useEffect(() => {
    const completed = tasks.filter(t => t.status === 'completed' && (t.audio_url || t.result_url));
    let shouldRefreshLibrary = false;
    completed.forEach(task => {
      const notifiedKey = `studio_refreshed_${task.id || task.task_id}`;
      try {
        const stored = JSON.parse(localStorage.getItem('beatlab52_studio_refreshed') || '[]');
        if (!stored.includes(notifiedKey)) {
          stored.push(notifiedKey);
          if (stored.length > 200) stored.splice(0, stored.length - 200);
          localStorage.setItem('beatlab52_studio_refreshed', JSON.stringify(stored));
          shouldRefreshLibrary = true;
        }
      } catch {
        shouldRefreshLibrary = true;
      }
    });
    if (shouldRefreshLibrary) {
      fetchSongLibraries();
    }
  }, [tasks]);

  const fetchSongLibraries = async () => {
    try {
      const res = await axios.get(`${API}/ai/songs`, { headers });
      setMusicLibrary(res.data.music_library || []);
      setAiLibrary(res.data.ai_library || []);
    } catch (error) {
      console.error('Error fetching songs:', error);
    }
  };

  const fetchTrash = async () => {
    try {
      const res = await axios.get(`${API}/trash`, { headers });
      setTrashItems(res.data || []);
    } catch (error) {
      console.error('Error fetching trash:', error);
    }
  };

  const restoreFromTrash = async (itemId) => {
    try {
      await axios.post(`${API}/trash/${itemId}/restore`, {}, { headers });
      toast.success('Song restored');
      fetchTrash();
      fetchSongLibraries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Restore failed');
    }
  };

  const permanentDelete = async (itemId) => {
    if (!confirm('Permanently delete this song? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/trash/${itemId}`, { headers });
      toast.success('Permanently deleted');
      fetchTrash();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const emptyTrash = async () => {
    if (!confirm('Permanently delete ALL items in trash? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/trash`, { headers });
      toast.success('Trash emptied');
      setTrashItems([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to empty trash');
    }
  };

  const fetchTasks = () => {
    refreshTasks();
  };

  const sunoOnlyTargets = ['wav_convert', 'persona', 'extend', 'replace_section', 'cover_image'];

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (sunoOnlyTargets.includes(songPickerTarget)) {
      toast.warning('This feature requires a Suno-generated track. Please select from the AI Library instead.');
      setShowSongPicker(false);
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setLoading(true);
      const res = await axios.post(`${API}/upload/music`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      
      const fileUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.file_url}`;
      
      if (songPickerTarget === 'stems') {
        setStemsUrl(fileUrl);
        setStemsFileName(file.name);
        setStemsTaskId('');
        setStemsAudioId('');
      } else if (songPickerTarget === 'cover') {
        setCoverUrl(fileUrl);
        setCoverFileName(file.name);
      } else if (songPickerTarget === 'master') {
        setMasterUrl(fileUrl);
        setMasterFileName(file.name);
      } else if (songPickerTarget === 'add_instrumental') {
        setAddInstrumentalUrl(fileUrl);
        setAddInstrumentalFileName(file.name);
      } else if (songPickerTarget === 'add_vocals') {
        setAddVocalsUrl(fileUrl);
        setAddVocalsFileName(file.name);
      } else if (songPickerTarget === 'mashup') {
        setMashupTracks(prev => [...prev, { url: fileUrl, name: file.name }]);
      }
      
      toast.success('File uploaded successfully');
      setShowSongPicker(false);
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const resolveAllIds = (song) => {
    const taskId = song.suno_task_id || song.task_id || '';
    const audioId = song.suno_audio_id || song.audio_id || '';
    const url = song.audio_url || song.file_url || '';
    return { taskId, audioId, url, title: song.title || 'Untitled' };
  };

  const CopyIdBadge = ({ label, value }) => {
    if (!value) return null;
    return (
      <span
        onClick={() => { navigator.clipboard.writeText(value).catch(() => {}); toast.success(`${label} copied!`); }}
        className="text-xs px-2 py-1 rounded font-mono flex items-center gap-1 cursor-pointer hover:brightness-150 transition-all"
        style={{ backgroundColor: `${theme.primary}15`, color: theme.primary, border: `1px solid ${theme.primary}30` }}
        title={`Click to copy: ${value}`}
      >
        {label}: {value.length > 16 ? value.slice(0, 16) + '...' : value} <Copy className="w-3 h-3" />
      </span>
    );
  };

  const selectSongFromLibrary = (song) => {
    const { url, title: name, taskId, audioId } = resolveAllIds(song);
    const needsTaskId = ['wav_convert', 'persona', 'extend', 'replace_section', 'cover_image'].includes(songPickerTarget);
    const needsAudioId = ['wav_convert', 'persona', 'extend', 'replace_section'].includes(songPickerTarget);

    if (needsTaskId && !taskId) {
      toast.warning(`No Task ID found for "${name}". This feature works best with AI-generated tracks.`);
    }
    if (needsAudioId && !audioId) {
      toast.warning(`No Audio ID found for "${name}". This feature works best with AI-generated tracks.`);
    }

    if (songPickerTarget === 'stems') {
      setStemsUrl(url);
      setStemsFileName(name);
      setStemsTaskId(taskId);
      setStemsAudioId(audioId);
    } else if (songPickerTarget === 'cover') {
      setCoverUrl(url);
      setCoverFileName(name);
    } else if (songPickerTarget === 'master') {
      setMasterUrl(url);
      setMasterFileName(name);
    } else if (songPickerTarget === 'extend') {
      setExtendUrl(url);
      setExtendAudioId(audioId);
      setExtendTaskId(taskId);
      setExtendSongTitle(name);
    } else if (songPickerTarget === 'add_instrumental') {
      setAddInstrumentalUrl(url);
      setAddInstrumentalFileName(name);
    } else if (songPickerTarget === 'add_vocals') {
      setAddVocalsUrl(url);
      setAddVocalsFileName(name);
    } else if (songPickerTarget === 'replace_section') {
      setReplaceSectionAudioId(audioId);
      setReplaceSectionTaskId(taskId);
      setReplaceSectionTitle(name);
    } else if (songPickerTarget === 'mashup') {
      setMashupTracks(prev => [...prev, { url, name }]);
    } else if (songPickerTarget === 'wav_convert') {
      setWavTaskId(taskId);
      setWavAudioId(audioId);
    } else if (songPickerTarget === 'upload_extend') {
      const urlEl = document.getElementById('upload-extend-url');
      if (urlEl) urlEl.value = url;
    } else if (songPickerTarget === 'persona') {
      setPersonaTaskId(taskId);
      setPersonaAudioId(audioId);
    } else if (songPickerTarget === 'cover_image') {
      setCoverImageTaskId(taskId);
    }

    setShowSongPicker(false);
    const idInfo = [];
    if (taskId) idInfo.push(`Task: ${taskId.slice(0, 12)}...`);
    if (audioId) idInfo.push(`Audio: ${audioId.slice(0, 12)}...`);
    toast.success(`Selected: ${name}${idInfo.length > 0 ? ` (${idInfo.join(', ')})` : ''}`);
  };

  const openSongPicker = (target) => {
    setSongPickerTarget(target);
    setShowSongPicker(true);
  };

  // Reference Track handlers for mastering
  const handleReferenceFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setLoading(true);
      const res = await axios.post(`${API}/upload/music`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      
      const fileUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.file_url}`;
      setReferenceTrackUrl(fileUrl);
      setReferenceTrackFileName(file.name);
      toast.success('Reference track uploaded successfully');
      setShowReferencePicker(false);
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const selectReferenceFromLibrary = (song) => {
    const url = song.source === 'ai' ? song.audio_url : song.file_url;
    const name = song.title;
    
    setReferenceTrackUrl(url);
    setReferenceTrackFileName(name);
    setShowReferencePicker(false);
    toast.success(`Reference selected: ${name}`);
  };

  // Open Magic Lyrics dialog
  const openMagicLyrics = (target) => {
    // Pre-fill value before dialog opens
    const currentPrompt = target === 'cover' ? coverFileName : getGenPrompt();
    setLyricsDialogTarget(target);
    // Clear the ref's value before showing dialog
    if (lyricsDescRef.current) {
      lyricsDescRef.current.value = currentPrompt || '';
    }
    setShowLyricsDialog(true);
  };

  // Generate lyrics from description
  const generateLyricsFromDescription = async () => {
    const description = getLyricsDescription();
    if (!description.trim()) {
      toast.error('Please describe the lyrics you want');
      return;
    }

    const forCover = lyricsDialogTarget === 'cover';
    if (forCover) {
      setGeneratingCoverLyrics(true);
    } else {
      setGeneratingLyrics(true);
    }
    setShowLyricsDialog(false);

    try {
      const res = await axios.post(`${API}/ai/generate-lyrics`, {
        prompt: description,
        music_style: genStyle,
        mood: 'energetic'
      }, { headers });

      if (res.data.lyrics) {
        if (forCover) {
          if (coverLyricsRef.current) coverLyricsRef.current.value = res.data.lyrics;
        } else {
          setGeneratedLyrics(res.data.lyrics);
        }
        toast.success('Lyrics generated based on your description!');
      }
    } catch (error) {
      toast.error('Failed to generate lyrics');
    } finally {
      if (forCover) {
        setGeneratingCoverLyrics(false);
      } else {
        setGeneratingLyrics(false);
      }
    }
  };

  // Generate lyrics - uses prompt if available, otherwise opens dialog
  const generateLyrics = async (forCover = false) => {
    const prompt = forCover ? coverFileName : getGenPrompt();
    if (!prompt) {
      // Open dialog to get description
      openMagicLyrics(forCover ? 'cover' : 'generate');
      return;
    }
    
    // If prompt exists, use it directly for lyrics generation
    if (forCover) {
      setGeneratingCoverLyrics(true);
    } else {
      setGeneratingLyrics(true);
    }
    
    try {
      const res = await axios.post(`${API}/ai/generate-lyrics`, {
        prompt: prompt,
        music_style: genStyle,
        mood: 'energetic'
      }, { headers });

      if (res.data.lyrics) {
        if (forCover) {
          if (coverLyricsRef.current) coverLyricsRef.current.value = res.data.lyrics;
        } else {
          setGeneratedLyrics(res.data.lyrics);
        }
        toast.success('Lyrics generated based on your prompt!');
      }
    } catch (error) {
      toast.error('Failed to generate lyrics');
    } finally {
      if (forCover) {
        setGeneratingCoverLyrics(false);
      } else {
        setGeneratingLyrics(false);
      }
    }
  };

  const handleGenerate = async () => {
    const prompt = getGenPrompt();
    const lyrics = getGenLyrics();

    setLoading(true);
    setResult(null);

    try {
      const endpoint = genProvider === 'suno' ? `${API}/ai/generate-suno` : `${API}/ai/generate`;
      
      const res = await axios.post(endpoint, {
        prompt: prompt,
        music_style: genStyle,
        custom_style_tags: customStyleTags || null,
        lyrics: lyrics || null,
        make_instrumental: isInstrumental,
        vocal_type: isInstrumental ? null : vocalType,
        model_version: modelVersion,
        output_length: outputLength && outputLength !== 'auto' ? parseInt(outputLength) : null,
        explicit: isExplicit,
      }, { headers });

      setResult({
        type: 'generate',
        message: res.data.message,
        taskId: res.data.task_id,
      });
      toast.success(genProvider === 'suno' ? 'Suno generation started! You\'ll get 2 versions.' : 'Generation started!');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const extendPromptLimit = extendModelVersion === 'V4' ? 3000 : 5000;
  const extendStyleLimit = extendModelVersion === 'V4' ? 200 : 1000;
  const extendTitleLimit = (extendModelVersion === 'V4' || extendModelVersion === 'V4_5ALL') ? 80 : 100;

  const handleExtend = async () => {
    if (!extendAudioId) {
      toast.error('Please select a Suno-generated song to extend');
      return;
    }
    setExtendLoading(true);
    try {
      const useOriginal = extendUseOriginal;
      const payload = {
        audio_id: extendAudioId,
        audio_url: extendUrl || '',
        model_version: extendModelVersion,
        default_param_flag: !useOriginal,
        vocal_type: extendVocalGender !== 'auto' ? extendVocalGender : null,
        negative_tags: extendNegativeTags || null,
        continue_at: parseFloat(extendContinueAt) || 60,
        instrumental: extendInstrumental,
        prompt: extendPrompt || '',
        style: extendStyle || 'Hip-Hop, Trap',
        title: extendTitle || (extendSongTitle ? extendSongTitle + ' (Extended)' : 'Extended Track'),
      };
      const res = await axios.post(`${API}/ai/extend`, payload, { headers });
      toast.success(res.data.message || 'Music extension started!');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Extension failed');
    } finally {
      setExtendLoading(false);
    }
  };

  const handleExtractStems = async () => {
    if (!stemsUrl.trim()) {
      toast.error('Please select a song');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const stemsPayload = {
        audio_url: stemsUrl,
        separation_type: stemType,
      };
      if (stemsTaskId) stemsPayload.task_id = stemsTaskId;
      if (stemsAudioId) stemsPayload.audio_id = stemsAudioId;
      const res = await axios.post(`${API}/ai/stems`, stemsPayload, { headers });

      setResult({
        type: 'stems',
        message: res.data.message,
        taskId: res.data.task_id,
      });
      toast.success('Stem extraction started!');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Extraction failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCover = async () => {
    if (!coverUrl.trim()) {
      toast.error('Please select a song');
      return;
    }

    setLoading(true);
    setResult(null);

    const selectedStyle = coverStyleOptions.flatMap(c => c.styles).find(s => s.id === coverStylePreset);
    const styleTags = coverCustomStyleTags || (selectedStyle ? selectedStyle.tags : '');

    try {
      const res = await axios.post(`${API}/ai/cover`, {
        audio_url: coverUrl,
        vocal_gender: coverVocalGender,
        style_tags: styleTags || null,
        make_instrumental: coverMakeInstrumental,
        keep_style_percent: keepStylePercent,
        similarity: similarity,
        custom_lyrics: getCoverLyrics() || null,
        title: coverTitle || null,
        genre: coverGenre !== 'auto' ? coverGenre : null,
        output_length: coverDuration !== 'auto' ? parseInt(coverDuration) : null,
        model_version: coverModelVersion,
      }, { headers });

      setResult({
        type: 'cover',
        message: res.data.message,
        taskId: res.data.task_id,
      });
      toast.success('Cover creation started!');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cover creation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMaster = async () => {
    if (!masterUrl.trim()) {
      toast.error('Please select a song to master');
      return;
    }
    
    // Use the referenceTrackUrl state (from picker/upload) or fallback to the text input
    const refUrl = referenceTrackUrl || getReferenceUrl();
    if (!refUrl.trim()) {
      toast.error('Reference track is required for mastering. Please select or upload a reference song.');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const res = await axios.post(`${API}/ai/master`, {
        audio_url: masterUrl,
        reference_url: refUrl,
        output_format: outputFormat,
      }, { headers });

      setResult({
        type: 'mastering',
        message: res.data.message,
        taskId: res.data.task_id,
      });
      toast.success('Mastering started!');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Mastering failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAddInstrumental = async () => {
    if (!addInstrumentalUrl.trim()) {
      toast.error('Please select a song');
      return;
    }
    setAddInstrumentalLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/ai/add-instrumental`, {
        audio_url: addInstrumentalUrl,
        title: addInstrumentalTitle || null,
        style: addInstrumentalStyle || null,
        model_version: addInstrumentalModel,
      }, { headers });
      setResult({ type: 'add_instrumental', message: res.data.message, taskId: res.data.task_id });
      toast.success(res.data.message || 'Adding instrumental started!');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Add instrumental failed');
    } finally {
      setAddInstrumentalLoading(false);
    }
  };

  const handleAddVocals = async () => {
    if (!addVocalsUrl.trim()) {
      toast.error('Please select a song');
      return;
    }
    setAddVocalsLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/ai/add-vocals`, {
        audio_url: addVocalsUrl,
        prompt: addVocalsPrompt || null,
        style: addVocalsStyle || null,
        vocal_gender: addVocalsGender,
        model_version: addVocalsModel,
      }, { headers });
      setResult({ type: 'add_vocals', message: res.data.message, taskId: res.data.task_id });
      toast.success(res.data.message || 'Adding vocals started!');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Add vocals failed');
    } finally {
      setAddVocalsLoading(false);
    }
  };

  const handleReplaceSection = async () => {
    if (!replaceSectionAudioId) {
      toast.error('Please select a Suno-generated track');
      return;
    }
    setReplaceSectionLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/ai/replace-section`, {
        audio_id: replaceSectionAudioId,
        prompt: replaceSectionPrompt || null,
        style: replaceSectionStyle || null,
        infill_start_s: parseFloat(replaceSectionStart) || 0,
        infill_end_s: parseFloat(replaceSectionEnd) || 30,
        model_version: replaceSectionModel,
      }, { headers });
      setResult({ type: 'replace_section', message: res.data.message, taskId: res.data.task_id });
      toast.success(res.data.message || 'Replace section started!');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Replace section failed');
    } finally {
      setReplaceSectionLoading(false);
    }
  };

  const handleMashup = async () => {
    if (mashupTracks.length < 2) {
      toast.error('At least 2 tracks are required for a mashup');
      return;
    }
    setMashupLoading(true);
    setResult(null);
    try {
      const payload = {
        upload_urls: mashupTracks.map(t => t.url),
        model_version: mashupModel,
        custom_mode: mashupCustomMode,
        instrumental: mashupInstrumental,
        title: mashupTitle || null,
      };
      if (mashupCustomMode) {
        payload.prompt = mashupPrompt || null;
        payload.style = mashupStyle || null;
        payload.vocal_gender = mashupVocalGender || null;
        payload.style_weight = parseFloat(mashupStyleWeight);
        payload.weirdness_constraint = parseFloat(mashupWeirdness);
        payload.audio_weight = parseFloat(mashupAudioWeight);
      }
      const res = await axios.post(`${API}/ai/mashup`, payload, { headers });
      setResult({ type: 'mashup', message: res.data.message, taskId: res.data.task_id });
      toast.success(res.data.message || 'Mashup started!');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Mashup failed');
    } finally {
      setMashupLoading(false);
    }
  };

  const handleWavConvert = async () => {
    if (!wavTaskId.trim() || !wavAudioId.trim()) {
      toast.error('Please enter both Task ID and Audio ID');
      return;
    }
    setWavLoading(true);
    setWavResult(null);
    try {
      const res = await axios.post(`${API}/ai/wav-convert`, {
        task_id: wavTaskId,
        audio_id: wavAudioId,
      }, { headers });
      if (res.data.wav_url) {
        setWavResult(res.data.wav_url);
        toast.success('WAV conversion complete!');
      } else {
        toast.success(res.data.message || 'WAV conversion started! Check tasks for progress.');
        fetchTasks();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'WAV conversion failed');
    } finally {
      setWavLoading(false);
    }
  };

  const handleSunoLyrics = async () => {
    if (!sunoLyricsPrompt.trim()) {
      toast.error('Please enter a lyrics prompt');
      return;
    }
    setSunoLyricsLoading(true);
    setSunoLyricsResult('');
    setSunoLyricsVariations([]);
    setSunoLyricsVariationIndex(0);
    try {
      const res = await axios.post(`${API}/ai/suno-lyrics`, {
        prompt: sunoLyricsPrompt,
      }, { headers, timeout: 90000 });
      const variations = res.data.variations || [];
      if (variations.length > 0) {
        setSunoLyricsVariations(variations);
        setSunoLyricsVariationIndex(0);
        setSunoLyricsResult(variations[0].text || '');
      } else {
        setSunoLyricsResult(res.data.lyrics || '');
      }
      toast.success(res.data.message || 'Lyrics generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Lyrics generation failed');
    } finally {
      setSunoLyricsLoading(false);
    }
  };

  const handleBoostStyle = async () => {
    if (!boostStyleContent.trim()) {
      toast.error('Please enter a style description to boost');
      return;
    }
    setBoostStyleLoading(true);
    setBoostStyleResult('');
    try {
      const res = await axios.post(`${API}/ai/boost-style`, {
        content: boostStyleContent,
      }, { headers });
      setBoostStyleResult(res.data.style || '');
      toast.success(res.data.message || 'Style boosted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Style boost failed');
    } finally {
      setBoostStyleLoading(false);
    }
  };

  const handlePersona = async () => {
    if (!personaTaskId.trim() || !personaAudioId.trim()) {
      toast.error('Task ID and Audio ID are required');
      return;
    }
    if (!personaName.trim()) {
      toast.error('Persona name is required');
      return;
    }
    if (!personaDescription.trim()) {
      toast.error('Persona description is required');
      return;
    }
    const vStart = parseFloat(personaVocalStart) || 0;
    const vEnd = parseFloat(personaVocalEnd) || 30;
    if (vStart >= vEnd) {
      toast.error('Vocal start must be less than vocal end');
      return;
    }
    const duration = vEnd - vStart;
    if (duration < 10 || duration > 30) {
      toast.error('Vocal segment must be between 10–30 seconds');
      return;
    }
    setPersonaLoading(true);
    setPersonaResult('');
    try {
      const payload = {
        task_id: personaTaskId,
        audio_id: personaAudioId,
        name: personaName,
        description: personaDescription,
        vocal_start: vStart,
        vocal_end: vEnd,
      };
      if (personaStyle.trim()) {
        payload.style = personaStyle.trim();
      }
      const res = await axios.post(`${API}/ai/persona`, payload, { headers });
      setPersonaResult(res.data.persona_id || '');
      toast.success(res.data.message || 'Persona created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Persona creation failed');
    } finally {
      setPersonaLoading(false);
    }
  };

  const deleteAiSong = async (songId) => {
    if (!isOwner) {
      toast.error('Only owners can delete songs');
      return;
    }
    if (!confirm('Move this song to trash?')) return;
    
    try {
      await axios.delete(`${API}/ai/library/${songId}`, { headers });
      toast.success('Song moved to trash');
      fetchSongLibraries();
      fetchTrash();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const deleteTask = async (taskId) => {
    if (!isOwner) {
      toast.error('Only owners can delete tasks');
      return;
    }
    if (!confirm('Delete this task?')) return;
    
    try {
      await axios.delete(`${API}/ai/task/${taskId}`, { headers });
      toast.success('Task deleted');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  // Magic Lyrics Dialog Component
  const magicLyricsDialogContent = (
    <Dialog open={showLyricsDialog} onOpenChange={setShowLyricsDialog}>
      <DialogContent 
        className="max-w-lg" 
        style={{ 
          backgroundColor: isDarkMode ? 'rgba(0,0,0,0.95)' : 'rgba(255,255,255,0.98)',
          borderColor: 'var(--border-color)',
        }}
        onOpenAutoFocus={(e) => {
          // Prevent default auto-focus and manually focus the textarea after a delay
          e.preventDefault();
          setTimeout(() => {
            if (lyricsDescRef.current) {
              lyricsDescRef.current.focus();
            }
          }, 300);
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: theme.primary }}>
            <Wand2 className="w-5 h-5" />
            Magic Lyrics Generator
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
              Describe the lyrics you want
            </label>
            <textarea
              ref={lyricsDescRef}
              placeholder="e.g., A song about chasing dreams in the city, with a hopeful chorus about never giving up..."
              className="min-h-[120px] flex w-full rounded-md border px-3 py-2 text-base placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              autoComplete="off"
              spellCheck="false"
              data-testid="lyrics-description-input"
            />
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
              Be descriptive! Include themes, emotions, and story elements.
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Style: {getEffectiveStyle()}</label>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Lyrics will be generated in {getEffectiveStyle()} style. Change style in the main form.
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              onClick={() => setShowLyricsDialog(false)}
              variant="outline"
              className="flex-1"
              style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            >
              Cancel
            </Button>
            <Button
              onClick={generateLyricsFromDescription}
              disabled={generatingLyrics || generatingCoverLyrics}
              className="flex-1 font-bold"
              style={{ backgroundColor: theme.primary, color: '#000' }}
            >
              {(generatingLyrics || generatingCoverLyrics) ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Wand2 className="w-4 h-4 mr-2" />
              )}
              Generate Lyrics
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  const songPickerDialogContent = (
    <Dialog open={showSongPicker} onOpenChange={setShowSongPicker}>
      <DialogContent 
        className="max-w-2xl" 
        style={{ 
          backgroundColor: isDarkMode ? 'rgba(0,0,0,0.95)' : 'rgba(255,255,255,0.98)',
          borderColor: 'var(--border-color)',
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: theme.primary }}>Select Song</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".mp3,.wav,.ogg,.flac,.m4a,.aac"
            className="hidden"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            className="w-full"
            variant="outline"
            style={{ borderColor: theme.primary, color: theme.primary }}
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload from Computer
          </Button>

          <div className="border-t" style={{ borderColor: 'var(--border-color)' }} />

          <div>
            <h3 className="font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Music Library</h3>
            <ScrollArea className="h-40">
              {musicLibrary.length > 0 ? (
                <div className="space-y-1">
                  {musicLibrary.map((song) => {
                    const ids = resolveAllIds(song);
                    return (
                      <div key={song.id} className="w-full text-left p-2 rounded hover:bg-white/10 group">
                        <button
                          onClick={() => selectSongFromLibrary(song)}
                          className="w-full text-left flex items-center gap-2"
                          style={{ color: 'var(--text-primary)' }}
                        >
                          <Music className="w-4 h-4 flex-shrink-0" style={{ color: theme.primary }} />
                          <span className="truncate">{song.title}</span>
                          {song.artist && <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-muted)' }}>- {song.artist}</span>}
                        </button>
                        {(ids.taskId || ids.audioId) && (
                          <div className="flex gap-2 mt-1 ml-6 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity">
                            {ids.taskId && (
                              <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(ids.taskId).catch(() => {}); toast.success('Task ID copied'); }} className="text-xs px-1.5 py-0.5 rounded font-mono flex items-center gap-1" style={{ backgroundColor: `${theme.primary}15`, color: theme.primary }}>
                                T: {ids.taskId.slice(0, 10)}... <Copy className="w-3 h-3" />
                              </button>
                            )}
                            {ids.audioId && (
                              <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(ids.audioId).catch(() => {}); toast.success('Audio ID copied'); }} className="text-xs px-1.5 py-0.5 rounded font-mono flex items-center gap-1" style={{ backgroundColor: `${theme.secondary || theme.primary}15`, color: theme.secondary || theme.primary }}>
                                A: {ids.audioId.slice(0, 10)}... <Copy className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center py-4" style={{ color: 'var(--text-muted)' }}>No songs in library</p>
              )}
            </ScrollArea>
          </div>

          <div>
            <h3 className="font-bold mb-2 flex items-center justify-between" style={{ color: 'var(--text-primary)' }}>
              AI Generated Library
              <span className="text-xs font-normal" style={{ color: 'var(--text-muted)' }}>Shared with all owners</span>
            </h3>
            <ScrollArea className="h-40">
              {aiLibrary.length > 0 ? (
                <div className="space-y-1">
                  {aiLibrary.map((song) => {
                    const songIsPlaying = isCurrentlyPlaying(song);
                    return (
                      <div key={song.id} className={`flex items-center gap-2 group ${songIsPlaying ? 'bg-white/10 rounded' : ''}`}>
                        <button
                          onClick={() => {
                            if (songIsPlaying) {
                              togglePlay();
                            } else {
                              playTrack({
                                id: song.id,
                                title: song.title,
                                artist: song.created_by || 'BeatLab52',
                                audio_url: song.audio_url,
                                album_cover: song.album_cover
                              });
                            }
                          }}
                          className="p-2 rounded-full hover:bg-white/20 transition"
                          style={{ color: theme.primary }}
                          title={songIsPlaying ? 'Pause' : 'Play'}
                        >
                          {songIsPlaying ? (
                            <AnimatedEqualizer color={theme.primary} size={16} />
                          ) : (
                            <Play className="w-4 h-4" fill="currentColor" />
                          )}
                        </button>
                        <button
                          onClick={() => selectSongFromLibrary(song)}
                          className="flex-1 text-left p-2 rounded hover:bg-white/10"
                          style={{ color: 'var(--text-primary)' }}
                        >
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 flex-shrink-0" style={{ color: theme.primary }} />
                            <span className="truncate">{song.title}</span>
                            {song.created_by && <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-muted)' }}>by {song.created_by}</span>}
                            {songIsPlaying && (
                              <span className="text-xs px-1.5 py-0.5 rounded-full ml-auto flex-shrink-0" style={{ backgroundColor: `${theme.primary}30`, color: theme.primary }}>
                                Playing
                              </span>
                            )}
                          </div>
                          {(() => { const ids = resolveAllIds(song); return (ids.taskId || ids.audioId) ? (
                            <div className="flex gap-2 mt-1 ml-6 flex-wrap opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity">
                              {ids.taskId && (
                                <span onClick={(e) => { e.stopPropagation(); e.preventDefault(); navigator.clipboard.writeText(ids.taskId).catch(() => {}); toast.success('Task ID copied'); }} className="text-xs px-1.5 py-0.5 rounded font-mono flex items-center gap-1 cursor-pointer hover:brightness-150" style={{ backgroundColor: `${theme.primary}15`, color: theme.primary }}>
                                  Task: {ids.taskId.slice(0, 12)}... <Copy className="w-3 h-3" />
                                </span>
                              )}
                              {ids.audioId && (
                                <span onClick={(e) => { e.stopPropagation(); e.preventDefault(); navigator.clipboard.writeText(ids.audioId).catch(() => {}); toast.success('Audio ID copied'); }} className="text-xs px-1.5 py-0.5 rounded font-mono flex items-center gap-1 cursor-pointer hover:brightness-150" style={{ backgroundColor: `${theme.secondary || theme.primary}15`, color: theme.secondary || theme.primary }}>
                                  Audio: {ids.audioId.slice(0, 12)}... <Copy className="w-3 h-3" />
                                </span>
                              )}
                            </div>
                          ) : null; })()}
                        </button>
                        <button
                          onClick={() => downloadFile(song.audio_url, `${song.title || 'track'}.mp3`)}
                          className="p-1 text-gray-400 hover:text-white opacity-0 group-hover:opacity-100 transition"
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        {isOwner && (
                          <button
                            onClick={() => deleteAiSong(song.id)}
                            className="p-1 text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center py-4" style={{ color: 'var(--text-muted)' }}>No AI songs yet</p>
              )}
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  // Reference Track Picker Dialog for Mastering
  const referencePickerDialogContent = (
    <Dialog open={showReferencePicker} onOpenChange={setShowReferencePicker}>
      <DialogContent 
        className="max-w-2xl" 
        style={{ 
          backgroundColor: isDarkMode ? 'rgba(0,0,0,0.95)' : 'rgba(255,255,255,0.98)',
          borderColor: 'var(--border-color)',
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: theme.primary }}>Select Reference Track</DialogTitle>
        </DialogHeader>
        
        <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: `${theme.primary}10`, borderColor: theme.primary, borderWidth: 1 }}>
          <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
            <strong>What is a reference track?</strong> A reference track is a professionally mastered song whose sound characteristics (loudness, EQ, dynamics) you want to match. Choose a track with a similar style to your song for best results.
          </p>
        </div>
        
        <div className="space-y-4">
          <input
            type="file"
            ref={referenceFileInputRef}
            onChange={handleReferenceFileUpload}
            accept=".mp3,.wav,.ogg,.flac,.m4a,.aac"
            className="hidden"
          />
          <Button
            onClick={() => referenceFileInputRef.current?.click()}
            className="w-full"
            variant="outline"
            style={{ borderColor: theme.primary, color: theme.primary }}
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Reference from Computer
          </Button>

          <div className="border-t" style={{ borderColor: 'var(--border-color)' }} />

          <div>
            <h3 className="font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Your Music Library</h3>
            <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>Select a professionally mastered track as reference</p>
            <ScrollArea className="h-40">
              {musicLibrary.length > 0 ? (
                <div className="space-y-1">
                  {musicLibrary.map((song) => (
                    <button
                      key={song.id}
                      onClick={() => selectReferenceFromLibrary(song)}
                      className="w-full text-left p-2 rounded hover:bg-white/10 flex items-center gap-2"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      <Music className="w-4 h-4" style={{ color: theme.primary }} />
                      <span className="truncate">{song.title}</span>
                      {song.artist && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>- {song.artist}</span>}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-center py-4" style={{ color: 'var(--text-muted)' }}>No songs in library</p>
              )}
            </ScrollArea>
          </div>

          <div>
            <h3 className="font-bold mb-2 flex items-center justify-between" style={{ color: 'var(--text-primary)' }}>
              AI Generated Library
              <span className="text-xs font-normal" style={{ color: 'var(--text-muted)' }}>Use AI tracks as reference</span>
            </h3>
            <ScrollArea className="h-40">
              {aiLibrary.length > 0 ? (
                <div className="space-y-1">
                  {aiLibrary.map((song) => (
                    <div key={song.id} className="flex items-center gap-2 group">
                      <button
                        onClick={() => playTrack({
                          title: song.title,
                          artist: song.created_by || 'BeatLab52',
                          audio_url: song.audio_url,
                          album_cover: song.album_cover
                        })}
                        className="p-2 rounded-full hover:bg-white/20 transition"
                        style={{ color: theme.primary }}
                        title="Play to preview"
                      >
                        <Play className="w-4 h-4" fill="currentColor" />
                      </button>
                      <button
                        onClick={() => selectReferenceFromLibrary({ ...song, source: 'ai' })}
                        className="flex-1 text-left p-2 rounded hover:bg-white/10 flex items-center gap-2"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        <Sparkles className="w-4 h-4" style={{ color: theme.primary }} />
                        <span className="truncate">{song.title}</span>
                        {song.created_by && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>by {song.created_by}</span>}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center py-4" style={{ color: 'var(--text-muted)' }}>No AI songs yet</p>
              )}
            </ScrollArea>
          </div>
          
          <div className="border-t pt-4" style={{ borderColor: 'var(--border-color)' }}>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Or enter a URL directly:</label>
            <div className="flex gap-2">
              <input
                ref={referenceUrlRef}
                type="text"
                defaultValue=""
                placeholder="https://example.com/reference.mp3"
                className="flex-1 h-10 rounded-md border px-3 py-2 text-base placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                autoComplete="off"
                spellCheck="false"
              />
              <Button
                onClick={() => {
                  const url = getReferenceUrl();
                  if (url.trim()) {
                    setReferenceTrackUrl(url);
                    setReferenceTrackFileName('External URL');
                    setShowReferencePicker(false);
                    toast.success('Reference URL set');
                  } else {
                    toast.error('Please enter a valid URL');
                  }
                }}
                style={{ backgroundColor: theme.primary, color: '#000' }}
              >
                Use URL
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  const sidebarCategories = [
    { key: 'create', label: 'CREATE', icon: <Sparkles className="w-3.5 h-3.5" />, items: [
      { tab: 'generate', label: 'Generate Music', icon: <Music className="w-4 h-4" /> },
      { tab: 'suno_lyrics', label: 'Generate Lyrics', icon: <FileText className="w-4 h-4" /> },
      { tab: 'sounds', label: 'Generate Sounds', icon: <AudioWaveform className="w-4 h-4" /> },
    ]},
    { key: 'remix', label: 'REMIX', icon: <Shuffle className="w-3.5 h-3.5" />, items: [
      { tab: 'extend', label: 'Extend Music', icon: <Maximize2 className="w-4 h-4" /> },
      { tab: 'cover', label: 'Music Cover', icon: <Mic className="w-4 h-4" /> },
      { tab: 'replace_section', label: 'Replace Section', icon: <Replace className="w-4 h-4" /> },
      { tab: 'mashup', label: 'Mashup Music', icon: <Shuffle className="w-4 h-4" /> },
      { tab: 'persona', label: 'Generate Persona', icon: <UserCircle className="w-4 h-4" /> },
      { tab: 'stems', label: 'Stem Separation', icon: <Scissors className="w-4 h-4" /> },
    ]},
    { key: 'upload', label: 'UPLOAD', icon: <Upload className="w-3.5 h-3.5" />, items: [
      { tab: 'upload_extend', label: 'Upload & Extend', icon: <FastForward className="w-4 h-4" /> },
      { tab: 'add_instrumental', label: 'Add Instrumental', icon: <Guitar className="w-4 h-4" /> },
      { tab: 'add_vocals', label: 'Add Vocals', icon: <MicVocal className="w-4 h-4" /> },
    ]},
    { key: 'tools', label: 'TOOLS', icon: <Wand2 className="w-3.5 h-3.5" />, items: [
      { tab: 'master', label: 'Mastering', icon: <Wand2 className="w-4 h-4" /> },
      { tab: 'cover_image', label: 'Cover Image', icon: <ImagePlus className="w-4 h-4" /> },
      { tab: 'boost_style', label: 'Boost Style', icon: <Zap className="w-4 h-4" /> },
      { tab: 'wav_convert', label: 'Convert to WAV', icon: <FileAudio className="w-4 h-4" /> },
    ]},
  ];

  const allFailedTasks = tasks.filter(t => t.status === 'failed' || t.status === 'not_found' || t.status === 'ERROR');
  const allProcessingTasks = tasks.filter(t => (t.status === 'processing' || t.status === 'pending' || t.status === 'queued') && !allFailedTasks.includes(t));
  const allCompletedTasks = tasks.filter(t => (t.status === 'completed' || (t.audio_url && !allFailedTasks.includes(t) && !allProcessingTasks.includes(t))));

  const getTaskIcon = (task) => {
    const type = (task.task_type || task.type || '').toLowerCase();
    if (type.includes('stem') || type.includes('separate')) return <Scissors className="w-4 h-4" />;
    if (type.includes('cover_image')) return <ImagePlus className="w-4 h-4" />;
    if (type.includes('cover')) return <Mic className="w-4 h-4" />;
    if (type.includes('master')) return <Wand2 className="w-4 h-4" />;
    if (type.includes('extend')) return <Maximize2 className="w-4 h-4" />;
    if (type.includes('add_instrumental') || type.includes('instrumental')) return <Guitar className="w-4 h-4" />;
    if (type.includes('add_vocal') || type.includes('vocal')) return <MicVocal className="w-4 h-4" />;
    if (type.includes('replace')) return <Replace className="w-4 h-4" />;
    if (type.includes('mashup')) return <Shuffle className="w-4 h-4" />;
    if (type.includes('lyric')) return <FileText className="w-4 h-4" />;
    if (type.includes('persona')) return <UserCircle className="w-4 h-4" />;
    if (type.includes('wav')) return <FileAudio className="w-4 h-4" />;
    return <Music className="w-4 h-4" />;
  };

  const getTaskLabel = (task) => {
    const type = (task.task_type || task.type || '').toLowerCase();
    if (type.includes('stem') || type.includes('separate')) return 'Stem Separation';
    if (type.includes('cover_image')) return 'Cover Image';
    if (type.includes('cover')) return 'AI Cover';
    if (type.includes('master')) return 'Mastering';
    if (type.includes('extend')) return 'Track Extension';
    if (type.includes('add_instrumental') || type.includes('instrumental')) return 'Add Instrumental';
    if (type.includes('add_vocal') || (type.includes('vocal') && !type.includes('separate'))) return 'Add Vocals';
    if (type.includes('replace')) return 'Replace Section';
    if (type.includes('mashup')) return 'Mashup';
    if (type.includes('lyric')) return 'Lyrics';
    if (type.includes('persona')) return 'Persona';
    if (type.includes('wav')) return 'WAV Convert';
    if (type.includes('music_ai') || type.includes('generation') || type.includes('suno')) return 'AI Generation';
    return 'AI Task';
  };

  const getTaskColor = (task) => {
    const type = (task.task_type || task.type || '').toLowerCase();
    if (type.includes('stem') || type.includes('separate')) return '#a78bfa';
    if (type.includes('cover')) return '#f472b6';
    if (type.includes('master')) return '#fbbf24';
    if (type.includes('extend')) return '#60a5fa';
    return theme.primary;
  };

  const handleSaveTaskToLibrary = async (task) => {
    try {
      const taskId = task.suno_task_id || task.task_id || task.id;
      const res = await axios.post(`${API}/ai/task/${taskId}/save-to-library`, {}, { headers });
      toast.success(res.data.message || 'Saved to AI Library!');
      fetchTasks();
      fetchSongLibraries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save to library');
    }
  };

  const handleDismissTask = async (task) => {
    try {
      const taskId = task.suno_task_id || task.task_id || task.id;
      await axios.delete(`${API}/ai/task/${taskId}`, { headers });
      toast.success('Task dismissed');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to dismiss task');
    }
  };

  return (
    <div className="flex h-[calc(100vh-64px)]" style={{ background: 'var(--bg-primary)' }}>
      {songPickerDialogContent}
      {referencePickerDialogContent}
      {magicLyricsDialogContent}

      {/* MOBILE SIDEBAR TOGGLE */}
      <button
        onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
        className="fixed top-[72px] left-2 z-50 p-2 rounded-lg lg:hidden"
        style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)' }}
      >
        <Menu className="w-5 h-5" style={{ color: theme.primary }} />
      </button>

      {/* LEFT SIDEBAR */}
      <aside
        className={`${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:relative z-40 lg:z-auto h-full flex-shrink-0 transition-transform duration-200 overflow-y-auto`}
        style={{ backgroundColor: 'var(--bg-card)', borderRight: '1px solid var(--border-color)', width: `${sidebarWidth}px` }}
      >
        <div className="px-4 py-4 flex items-center gap-2" style={{ borderBottom: '1px solid var(--border-color)' }}>
          <Sparkles className="w-5 h-5" style={{ color: theme.primary }} />
          <div>
            <h1 className="text-sm font-black uppercase tracking-wider" style={{ color: theme.primary }}>AI Studio</h1>
            <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>DJ808 & CRG Productionz</p>
          </div>
          <button onClick={() => setMobileSidebarOpen(false)} className="ml-auto lg:hidden p-1">
            <PanelLeftClose className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
          </button>
        </div>

        <nav className="py-2">
          {sidebarCategories.map((cat) => (
            <div key={cat.key} className="mb-1">
              <button
                onClick={() => toggleCategory(cat.key)}
                className="w-full flex items-center gap-2 px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest hover:bg-white/5 transition"
                style={{ color: 'var(--text-muted)' }}
              >
                {sidebarCollapsed[cat.key] ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {cat.icon}
                {cat.label}
              </button>
              {!sidebarCollapsed[cat.key] && (
                <div className="mt-0.5">
                  {cat.items.map((item) => (
                    <button
                      key={item.tab}
                      onClick={() => { setActiveTab(item.tab); setMobileSidebarOpen(false); }}
                      className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm transition ${activeTab === item.tab ? 'font-semibold' : 'hover:bg-white/5'}`}
                      style={{
                        color: activeTab === item.tab ? theme.primary : 'var(--text-secondary)',
                        backgroundColor: activeTab === item.tab ? `${theme.primary}10` : 'transparent',
                        borderLeft: activeTab === item.tab ? `3px solid ${theme.primary}` : '3px solid transparent',
                      }}
                    >
                      {item.icon}
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}

          <div className="mt-2 pt-2" style={{ borderTop: '1px solid var(--border-color)' }}>
            <button
              onClick={() => { setActiveTab('library'); fetchSongLibraries(); setMobileSidebarOpen(false); }}
              className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm transition ${activeTab === 'library' ? 'font-semibold' : 'hover:bg-white/5'}`}
              style={{
                color: activeTab === 'library' ? theme.primary : 'var(--text-secondary)',
                backgroundColor: activeTab === 'library' ? `${theme.primary}10` : 'transparent',
                borderLeft: activeTab === 'library' ? `3px solid ${theme.primary}` : '3px solid transparent',
              }}
            >
              <FolderOpen className="w-4 h-4" />
              AI Library
              {aiLibrary.length > 0 && <span className="ml-auto text-xs px-1.5 py-0.5 rounded-full bg-purple-500/20 text-purple-400">{aiLibrary.length}</span>}
            </button>
            <button
              onClick={() => { setActiveTab('trash'); fetchTrash(); setMobileSidebarOpen(false); }}
              className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm transition ${activeTab === 'trash' ? 'font-semibold' : 'hover:bg-white/5'}`}
              style={{
                color: activeTab === 'trash' ? '#ef4444' : 'var(--text-secondary)',
                backgroundColor: activeTab === 'trash' ? 'rgba(239,68,68,0.1)' : 'transparent',
                borderLeft: activeTab === 'trash' ? '3px solid #ef4444' : '3px solid transparent',
              }}
            >
              <Trash2 className="w-4 h-4" />
              Trash
              {trashItems.length > 0 && <span className="ml-auto text-xs px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400">{trashItems.length}</span>}
            </button>
          </div>
        </nav>
      </aside>

      {/* SIDEBAR RESIZE HANDLE */}
      <div
        className="hidden lg:flex w-1.5 flex-shrink-0 cursor-col-resize items-center justify-center group hover:bg-white/5 transition"
        onMouseDown={(e) => handleResizeStart('sidebar', e)}
        style={{ backgroundColor: 'transparent' }}
      >
        <div className="w-0.5 h-8 rounded-full bg-white/10 group-hover:bg-white/30 transition" />
      </div>

      {/* MOBILE SIDEBAR OVERLAY */}
      {mobileSidebarOpen && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setMobileSidebarOpen(false)} />}

      {/* CENTER PANEL - FORM/PROMPT AREA */}
      <main className="flex-1 overflow-y-auto min-w-0">
        <div className="max-w-3xl mx-auto px-3 sm:px-6 py-4">

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="hidden"></TabsList>

          {/* Generate Tab */}
          <TabsContent value="generate">
            <GlassCard>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.primary }}>
                <Music className="w-5 h-5" />
                Generate Music
              </h2>
              
              {/* AI Provider Toggle */}
              <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>AI Provider</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => aiConfig?.suno?.enabled && setGenProvider('suno')}
                    className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition ${genProvider === 'suno' ? 'ring-2' : 'opacity-70'} ${!aiConfig?.suno?.enabled ? 'cursor-not-allowed opacity-40' : ''}`}
                    style={{ 
                      backgroundColor: genProvider === 'suno' ? `${theme.primary}20` : 'var(--bg-tertiary)',
                      color: genProvider === 'suno' ? theme.primary : 'var(--text-muted)',
                      ringColor: theme.primary
                    }}
                  >
                    <Sparkles className="w-4 h-4 inline mr-2" />
                    Suno AI (2 versions)
                    {aiConfig?.suno?.enabled && <Check className="w-3 h-3 inline ml-1 text-green-400" />}
                  </button>
                  <button
                    onClick={() => aiConfig?.musicgpt?.enabled && setGenProvider('musicgpt')}
                    className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition ${genProvider === 'musicgpt' ? 'ring-2' : 'opacity-70'} ${!aiConfig?.musicgpt?.enabled ? 'cursor-not-allowed opacity-40' : ''}`}
                    style={{ 
                      backgroundColor: genProvider === 'musicgpt' ? `${theme.secondary}20` : 'var(--bg-tertiary)',
                      color: genProvider === 'musicgpt' ? theme.secondary : 'var(--text-muted)',
                      ringColor: theme.secondary
                    }}
                  >
                    <Wand2 className="w-4 h-4 inline mr-2" />
                    MusicGPT
                    {aiConfig?.musicgpt?.enabled && <Check className="w-3 h-3 inline ml-1 text-green-400" />}
                  </button>
                </div>
                <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                  {genProvider === 'suno' ? '✨ Suno returns 2 track versions per generation' : '🎵 MusicGPT generates single tracks'}
                </p>
              </div>
              
              <div className="space-y-4">
                {/* Model Version */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                      {genProvider === 'suno' ? 'Suno Model' : 'Model Version'}
                    </label>
                    <Select value={modelVersion} onValueChange={setModelVersion}>
                      <SelectTrigger style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent style={{ backgroundColor: isDarkMode ? '#000' : '#fff' }}>
                        {genProvider === 'suno' ? (
                          <>
                            <SelectItem value="V5_5">V5.5 (Latest & Best)</SelectItem>
                            <SelectItem value="V5">V5 (Superior Expression)</SelectItem>
                            <SelectItem value="V4_5PLUS">V4.5+ (Max 8 min)</SelectItem>
                            <SelectItem value="V4_5">V4.5 (Faster)</SelectItem>
                            <SelectItem value="V4">V4 (Better Vocals)</SelectItem>
                          </>
                        ) : (
                          modelVersions.map((v) => (
                            <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Genre</label>
                    <Select value={selectedMainGenre} onValueChange={handleMainGenreChange}>
                      <SelectTrigger style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent style={{ backgroundColor: isDarkMode ? '#000' : '#fff' }} className="max-h-[300px]">
                        {mainGenres.map((genre) => (
                          <SelectItem key={genre} value={genre}>{genre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {genreMap[selectedMainGenre] && genreMap[selectedMainGenre].length > 0 && (
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                      Sub-Genre
                      <span className="text-xs font-normal ml-2" style={{ color: 'var(--text-muted)' }}>(optional — narrows the style)</span>
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        onClick={() => handleSubGenreChange('')}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${!selectedSubGenre ? 'ring-2' : 'hover:bg-white/10'}`}
                        style={{
                          backgroundColor: !selectedSubGenre ? `${theme.primary}20` : 'rgba(255,255,255,0.05)',
                          color: !selectedSubGenre ? theme.primary : 'var(--text-muted)',
                          '--tw-ring-color': theme.primary,
                        }}
                      >
                        All {selectedMainGenre}
                      </button>
                      {genreMap[selectedMainGenre].map((sub) => (
                        <button
                          key={sub}
                          onClick={() => handleSubGenreChange(sub)}
                          className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${selectedSubGenre === sub ? 'ring-2' : 'hover:bg-white/10'}`}
                          style={{
                            backgroundColor: selectedSubGenre === sub ? `${theme.primary}20` : 'rgba(255,255,255,0.05)',
                            color: selectedSubGenre === sub ? theme.primary : 'var(--text-muted)',
                            '--tw-ring-color': theme.primary,
                          }}
                        >
                          {sub}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Song Duration */}
                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center gap-1" style={{ color: 'var(--text-primary)' }}>
                    <Clock className="w-4 h-4" />
                    Song Duration
                  </label>
                  <Select value={outputLength} onValueChange={setOutputLength}>
                    <SelectTrigger style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
                      <SelectValue placeholder="Auto (Default)" />
                    </SelectTrigger>
                    <SelectContent style={{ backgroundColor: isDarkMode ? '#000' : '#fff' }}>
                      <SelectItem value="auto">Auto (Default)</SelectItem>
                      <SelectItem value="20">20 Seconds</SelectItem>
                      <SelectItem value="60">1 Minute</SelectItem>
                      <SelectItem value="155">2:35</SelectItem>
                      <SelectItem value="180">3 Minutes</SelectItem>
                      <SelectItem value="230">3:50</SelectItem>
                      <SelectItem value="300">Full Song (5 min)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Custom Style Tags */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Style Tags / Description
                  </label>
                  <textarea
                    value={customStyleTags}
                    onChange={(e) => setCustomStyleTags(e.target.value)}
                    placeholder={`Describe the vibe, energy, and sound you want...\ne.g. "808 heavy bass, dark trap, aggressive energy, Memphis phonk, hard-hitting drums, distorted synths"`}
                    className="min-h-[70px] flex w-full rounded-md border px-3 py-2 text-base placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    maxLength={200}
                  />
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                    {genProvider === 'suno' 
                      ? 'These tags tell Suno the exact style, mood, and instruments to use (separate with commas)' 
                      : 'Describe the production style for MusicGPT'}
                  </p>
                </div>

                {/* Prompt */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    {genProvider === 'suno' ? 'Song Title / Theme' : 'Prompt'}
                  </label>
                  <textarea
                    ref={genPromptRef}
                    defaultValue=""
                    onInput={handlePromptInput}
                    placeholder={genProvider === 'suno' 
                      ? "What's the song about? e.g., 'Riding through the city at night' or 'Gulf Coast summer vibes'" 
                      : "Describe the music you want to create... e.g., 'An energetic hip-hop beat with heavy 808s'"}
                    className="min-h-[80px] flex w-full rounded-md border px-3 py-2 text-base placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    maxLength={500}
                    autoComplete="off"
                    spellCheck="false"
                    data-testid="ai-studio-prompt"
                  />
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                    {genProvider === 'suno' 
                      ? 'This is the song title/theme — NOT lyrics. Put actual lyrics in the Custom Lyrics box below.' 
                      : `${promptCharCount}/500`}
                  </p>
                </div>

                {/* Instrumental / Vocals + Explicit toggle */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={isInstrumental}
                      onCheckedChange={setIsInstrumental}
                    />
                    <label className="text-sm" style={{ color: 'var(--text-primary)' }}>
                      {isInstrumental ? <VolumeX className="w-4 h-4 inline mr-1" /> : <Volume2 className="w-4 h-4 inline mr-1" />}
                      {isInstrumental ? 'Instrumental Only' : 'With Vocals'}
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={isExplicit}
                      onCheckedChange={setIsExplicit}
                    />
                    <label className="text-sm flex items-center gap-1" style={{ color: isExplicit ? '#ef4444' : 'var(--text-primary)' }}>
                      {isExplicit ? (
                        <><span className="inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold bg-red-500/20 text-red-400 border border-red-500/30">E</span> Explicit (Raw Lyrics)</>
                      ) : (
                        <><span className="inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold bg-green-500/20 text-green-400 border border-green-500/30">C</span> Clean (Auto-Sanitize)</>
                      )}
                    </label>
                  </div>
                  
                  {!isInstrumental && (
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Vocal Type</label>
                      <Select value={vocalType} onValueChange={setVocalType}>
                        <SelectTrigger style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
                          <SelectValue placeholder="Select vocal type" />
                        </SelectTrigger>
                        <SelectContent style={{ backgroundColor: isDarkMode ? '#000' : '#fff' }}>
                          {vocalTypes.map((v) => (
                            <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Lyrics */}
                {!isInstrumental && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Custom Lyrics (Optional)</label>
                      <Button
                        onClick={() => getGenPrompt() ? generateLyrics(false) : openMagicLyrics('generate')}
                        disabled={generatingLyrics}
                        size="sm"
                        variant="outline"
                        style={{ borderColor: theme.secondary, color: theme.secondary }}
                        data-testid="magic-lyrics-btn"
                      >
                        {generatingLyrics ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}
                        Magic Lyrics
                      </Button>
                    </div>
                    <textarea
                      ref={genLyricsRef}
                      defaultValue=""
                      onInput={handleLyricsInput}
                      placeholder="Enter custom lyrics or click Magic Lyrics to generate..."
                      className="min-h-[120px] flex w-full rounded-md border px-3 py-2 text-base placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                      autoComplete="off"
                      spellCheck="false"
                      data-testid="ai-studio-lyrics"
                    />
                  </div>
                )}

                <Button
                  onClick={handleGenerate}
                  disabled={loading}
                  className="w-full font-bold uppercase tracking-wider"
                  style={{ backgroundColor: theme.primary, color: '#000' }}
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Generate Music
                </Button>
              </div>
            </GlassCard>
          </TabsContent>

          {/* Stems Tab */}
          <TabsContent value="stems">
            <GlassCard>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.primary }}>
                <Scissors className="w-5 h-5" />
                Extract Stems
              </h2>
              
              {!aiConfig?.suno?.enabled ? (
                <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: 'rgba(239,68,68,0.1)', borderColor: '#ef4444' }}>
                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    <strong>Suno API key required.</strong> Add SUNO_API_KEY in Secrets to enable stem separation.
                  </p>
                </div>
              ) : (
                <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: `${theme.secondary}10`, borderColor: theme.secondary }}>
                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    <strong>Powered by Suno AI:</strong> Extract vocals, drums, bass, and more from any track.
                    Works best with tracks generated through Suno, but you can also upload external audio.
                  </p>
                </div>
              )}
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Select Song</label>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => openSongPicker('stems')}
                      variant="outline"
                      className="flex-1"
                      style={{ borderColor: theme.primary, color: theme.primary }}
                    >
                      <FolderOpen className="w-4 h-4 mr-2" />
                      {stemsFileName || 'Browse Library / Upload'}
                    </Button>
                    {stemsFileName && (
                      <Button variant="ghost" onClick={() => { setStemsUrl(''); setStemsFileName(''); setStemsTaskId(''); setStemsAudioId(''); }}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  {(stemsTaskId || stemsAudioId) && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      <CopyIdBadge label="Task ID" value={stemsTaskId} />
                      <CopyIdBadge label="Audio ID" value={stemsAudioId} />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Separation Type</label>
                  <Select value={stemType} onValueChange={setStemType}>
                    <SelectTrigger style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent style={{ backgroundColor: isDarkMode ? '#000' : '#fff' }}>
                      <SelectItem value="separate_vocal">2-Stem (Vocals + Instrumental)</SelectItem>
                      <SelectItem value="split_stem">Multi-Stem (Vocals, Drums, Bass, Guitar, Keys, Strings, Brass, Woodwinds, FX & more)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  onClick={handleExtractStems}
                  disabled={loading || !stemsUrl}
                  className="w-full font-bold uppercase tracking-wider"
                  style={{ backgroundColor: theme.primary, color: '#000' }}
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Scissors className="w-4 h-4 mr-2" />}
                  Extract Stems
                </Button>
              </div>
            </GlassCard>

            {/* Completed Stems Projects */}
            {(() => {
              const stemTasks = tasks.filter(t => {
                const type = (t.task_type || t.type || '').toLowerCase();
                const isStem = type.includes('stem') || type.includes('separate') || (type.includes('vocal') && type.includes('remov'));
                const isComplete = t.status === 'completed' || (t.audio_url && t.status !== 'failed');
                const s = typeof t.settings === 'string' ? (() => { try { return JSON.parse(t.settings); } catch { return {}; } })() : (t.settings || {});
                return isStem && isComplete && s.stem_urls && Object.keys(s.stem_urls).length > 0;
              });
              if (stemTasks.length === 0) return null;
              const sorted = [...stemTasks].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
              return (
                <div className="mt-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Scissors className="w-4 h-4" style={{ color: '#a78bfa' }} />
                    <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: '#a78bfa' }}>
                      Stem Projects ({sorted.length})
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {sorted.map((task) => {
                      const s = typeof task.settings === 'string' ? (() => { try { return JSON.parse(task.settings); } catch { return {}; } })() : (task.settings || {});
                      const stems = s.stem_urls || {};
                      const stemEntries = Object.entries(stems).filter(([,v]) => v);
                      const projectTitle = task.title || task.prompt?.slice(0, 40) || 'Stem Separation';
                      const taskId = task.suno_task_id || task.task_id || task.id;
                      return (
                        <ContextMenu key={task.id}>
                          <ContextMenuTrigger asChild>
                            <div className="rounded-xl overflow-hidden cursor-context-menu" style={{ border: '1px solid rgba(167,139,250,0.2)', background: 'var(--bg-card)' }}>
                              <div className="px-4 py-2.5 flex items-center gap-3" style={{ background: 'rgba(167,139,250,0.06)' }}>
                                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(167,139,250,0.15)' }}>
                                  <Scissors className="w-3.5 h-3.5 text-purple-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <span className="text-sm font-semibold text-purple-300 truncate block">{projectTitle}</span>
                                  <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                                    {stemEntries.length} stems {task.created_at && ` \u2022 ${new Date(task.created_at).toLocaleString()}`}
                                  </span>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    stemEntries.forEach(([name, url]) => {
                                      downloadFile(url, `${projectTitle}_${name.replace('_url','')}.mp3`);
                                    });
                                    toast.success(`Downloading ${stemEntries.length} stems`);
                                  }}
                                  className="text-xs px-2.5 py-1 rounded-lg bg-purple-500/15 text-purple-300 hover:bg-purple-500/25 transition flex items-center gap-1.5"
                                >
                                  <Download className="w-3 h-3" /> All
                                </button>
                              </div>
                              <div className="px-3 py-2 grid gap-1.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}>
                                {stemEntries.map(([name, url]) => {
                                  const label = name.replace('_url','').replace(/_/g, ' ');
                                  return (
                                    <button
                                      key={name}
                                      onClick={() => {
                                        try {
                                          playTrack({ url, title: `${projectTitle} - ${label}`, artist: task.username || 'AI' });
                                        } catch {}
                                      }}
                                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-left hover:bg-white/5 transition group/stem"
                                      style={{ border: '1px solid rgba(167,139,250,0.1)' }}
                                    >
                                      <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0 bg-purple-500/15">
                                        <Play className="w-3 h-3 text-purple-400 group-hover/stem:text-purple-300" />
                                      </div>
                                      <span className="text-xs font-medium truncate capitalize" style={{ color: 'var(--text-primary)' }}>{label}</span>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); downloadFile(url, `${projectTitle}_${label}.mp3`); }}
                                        className="ml-auto opacity-0 group-hover/stem:opacity-100 transition p-0.5"
                                      >
                                        <Download className="w-3 h-3 text-purple-400" />
                                      </button>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </ContextMenuTrigger>
                          <ContextMenuContent className="w-56 bg-gray-900/95 border-white/10 backdrop-blur-xl">
                            <ContextMenuItem
                              onClick={async () => {
                                try {
                                  const res = await axios.post(`${API}/ai/task/${taskId}/save-to-library`, {}, { headers });
                                  toast.success(res.data.message || 'Stems saved to AI Library!');
                                  fetchTasks();
                                  fetchSongLibraries();
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || 'Failed to save to library');
                                }
                              }}
                              className="flex items-center gap-2 text-gray-200 hover:text-white focus:bg-white/10 focus:text-white cursor-pointer"
                            >
                              <FolderPlus className="w-4 h-4 text-green-400" /> Save to AI Library
                            </ContextMenuItem>
                            <ContextMenuItem
                              onClick={() => {
                                stemEntries.forEach(([name, url]) => {
                                  downloadFile(url, `${projectTitle}_${name.replace('_url','')}.mp3`);
                                });
                                toast.success(`Downloading ${stemEntries.length} stems`);
                              }}
                              className="flex items-center gap-2 text-gray-200 hover:text-white focus:bg-white/10 focus:text-white cursor-pointer"
                            >
                              <Download className="w-4 h-4 text-purple-400" /> Download All Stems
                            </ContextMenuItem>
                            <ContextMenuItem
                              onClick={() => { navigator.clipboard.writeText(taskId); toast.success('Task ID copied!'); }}
                              className="flex items-center gap-2 text-gray-200 hover:text-white focus:bg-white/10 focus:text-white cursor-pointer"
                            >
                              <Copy className="w-4 h-4 text-cyan-400" /> Copy Task ID
                            </ContextMenuItem>
                            <ContextMenuSeparator className="bg-white/10" />
                            <ContextMenuItem
                              onClick={async () => {
                                try {
                                  await axios.delete(`${API}/ai/task/${taskId}`, { headers });
                                  toast.success('Stem project dismissed');
                                  fetchTasks();
                                } catch { toast.error('Failed to dismiss'); }
                              }}
                              className="flex items-center gap-2 text-red-400 hover:text-red-300 focus:bg-red-500/10 focus:text-red-300 cursor-pointer"
                            >
                              <XCircle className="w-4 h-4" /> Dismiss
                            </ContextMenuItem>
                          </ContextMenuContent>
                        </ContextMenu>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </TabsContent>

          {/* Cover Tab */}
          <TabsContent value="cover">
            <GlassCard>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.primary }}>
                <Mic className="w-5 h-5" />
                Create AI Cover
              </h2>
              
              {!aiConfig?.suno?.enabled ? (
                <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: 'rgba(239,68,68,0.1)', borderColor: '#ef4444' }}>
                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    <strong>Suno API key required.</strong> Add SUNO_API_KEY in Secrets to enable AI covers.
                  </p>
                </div>
              ) : (
                <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: `${theme.primary}10`, borderColor: theme.primary }}>
                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    <strong>Powered by Suno AI:</strong> Transform any audio into a new style while keeping the melody. 
                    Select a style below to define the sound of your cover.
                  </p>
                </div>
              )}
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Select Song</label>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => openSongPicker('cover')}
                      variant="outline"
                      className="flex-1"
                      style={{ borderColor: theme.primary, color: theme.primary }}
                    >
                      <FolderOpen className="w-4 h-4 mr-2" />
                      {coverFileName || 'Browse Library / Upload'}
                    </Button>
                    {coverFileName && (
                      <Button variant="ghost" onClick={() => { setCoverUrl(''); setCoverFileName(''); }}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Cover Style</label>
                    <Select value={coverStylePreset} onValueChange={setCoverStylePreset}>
                      <SelectTrigger 
                        className="w-full" 
                        style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                        data-testid="cover-style-select"
                      >
                        <SelectValue placeholder="Select a style" />
                      </SelectTrigger>
                      <SelectContent style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
                        {coverStyleOptions.map((category) => (
                          <div key={category.category}>
                            <div className="px-2 py-1.5 text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
                              {category.category}
                            </div>
                            {category.styles.map((style) => (
                              <SelectItem key={style.id} value={style.id} style={{ color: 'var(--text-primary)' }}>
                                {style.name}
                              </SelectItem>
                            ))}
                          </div>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Vocal Gender</label>
                    <Select value={coverVocalGender} onValueChange={setCoverVocalGender}>
                      <SelectTrigger className="w-full" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent style={{ backgroundColor: isDarkMode ? '#000' : '#fff' }}>
                        <SelectItem value="m">Male</SelectItem>
                        <SelectItem value="f">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Suno Model</label>
                    <Select value={coverModelVersion} onValueChange={setCoverModelVersion}>
                      <SelectTrigger className="w-full" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent style={{ backgroundColor: isDarkMode ? '#000' : '#fff' }}>
                        <SelectItem value="V5_5">V5.5 (Latest & Best)</SelectItem>
                        <SelectItem value="V5">V5 (Superior Expression)</SelectItem>
                        <SelectItem value="V4_5PLUS">V4.5+ (Max 8 min)</SelectItem>
                        <SelectItem value="V4_5">V4.5 (Faster)</SelectItem>
                        <SelectItem value="V4">V4 (Better Vocals)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Pitch Shift: {pitch > 0 ? '+' : ''}{pitch}</label>
                    <Slider
                      value={[pitch]}
                      onValueChange={([v]) => setPitch(v)}
                      min={-12}
                      max={12}
                      step={1}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Cover Title</label>
                    <input
                      value={coverTitle || ''}
                      onChange={(e) => setCoverTitle(e.target.value)}
                      placeholder="e.g. My Fire Cover"
                      className="flex w-full rounded-md border px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Genre</label>
                    <Select value={coverGenre} onValueChange={setCoverGenre}>
                      <SelectTrigger className="w-full" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                        <SelectValue placeholder="Auto" />
                      </SelectTrigger>
                      <SelectContent style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
                        <SelectItem value="auto" style={{ color: 'var(--text-primary)' }}>Auto (from voice)</SelectItem>
                        <SelectItem value="Hip-Hop" style={{ color: 'var(--text-primary)' }}>Hip-Hop</SelectItem>
                        <SelectItem value="Trap" style={{ color: 'var(--text-primary)' }}>Trap</SelectItem>
                        <SelectItem value="Crunk" style={{ color: 'var(--text-primary)' }}>Crunk</SelectItem>
                        <SelectItem value="R&B" style={{ color: 'var(--text-primary)' }}>R&B</SelectItem>
                        <SelectItem value="Pop" style={{ color: 'var(--text-primary)' }}>Pop</SelectItem>
                        <SelectItem value="Rock" style={{ color: 'var(--text-primary)' }}>Rock</SelectItem>
                        <SelectItem value="Country" style={{ color: 'var(--text-primary)' }}>Country</SelectItem>
                        <SelectItem value="Jazz" style={{ color: 'var(--text-primary)' }}>Jazz</SelectItem>
                        <SelectItem value="Electronic" style={{ color: 'var(--text-primary)' }}>Electronic</SelectItem>
                        <SelectItem value="Latin" style={{ color: 'var(--text-primary)' }}>Latin</SelectItem>
                        <SelectItem value="Reggae" style={{ color: 'var(--text-primary)' }}>Reggae</SelectItem>
                        <SelectItem value="Afrobeat" style={{ color: 'var(--text-primary)' }}>Afrobeat</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Duration</label>
                    <Select value={coverDuration} onValueChange={setCoverDuration}>
                      <SelectTrigger className="w-full" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                        <SelectValue placeholder="Auto" />
                      </SelectTrigger>
                      <SelectContent style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
                        <SelectItem value="auto" style={{ color: 'var(--text-primary)' }}>Auto</SelectItem>
                        <SelectItem value="20" style={{ color: 'var(--text-primary)' }}>20s</SelectItem>
                        <SelectItem value="60" style={{ color: 'var(--text-primary)' }}>1 min</SelectItem>
                        <SelectItem value="155" style={{ color: 'var(--text-primary)' }}>2:35</SelectItem>
                        <SelectItem value="180" style={{ color: 'var(--text-primary)' }}>3 min</SelectItem>
                        <SelectItem value="230" style={{ color: 'var(--text-primary)' }}>3:50</SelectItem>
                        <SelectItem value="300" style={{ color: 'var(--text-primary)' }}>Full Song</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Keep Style: {keepStylePercent}%</label>
                    <Slider
                      value={[keepStylePercent]}
                      onValueChange={([v]) => setKeepStylePercent(v)}
                      min={0}
                      max={100}
                      step={5}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Similarity: {similarity}%</label>
                    <Slider
                      value={[similarity]}
                      onValueChange={([v]) => setSimilarity(v)}
                      min={0}
                      max={100}
                      step={5}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Custom Style Tags (Optional)</label>
                    <input
                      value={coverCustomStyleTags}
                      onChange={(e) => setCoverCustomStyleTags(e.target.value)}
                      placeholder="e.g. deep bass, autotune, dark atmosphere"
                      className="flex w-full rounded-md border px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    />
                    <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                      Override the preset style with your own tags
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={coverMakeInstrumental}
                      onCheckedChange={setCoverMakeInstrumental}
                    />
                    <label className="text-sm" style={{ color: 'var(--text-primary)' }}>Instrumental / Beat Only</label>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Custom Lyrics (Optional)</label>
                    <Button
                      onClick={() => coverFileName ? generateLyrics(true) : openMagicLyrics('cover')}
                      disabled={generatingCoverLyrics}
                      size="sm"
                      variant="outline"
                      style={{ borderColor: theme.secondary, color: theme.secondary }}
                      data-testid="cover-magic-lyrics-btn"
                    >
                      {generatingCoverLyrics ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}
                      Magic Lyrics
                    </Button>
                  </div>
                  <textarea
                    ref={coverLyricsRef}
                    defaultValue=""
                    placeholder="Enter custom lyrics for the cover or click Magic Lyrics..."
                    className="min-h-[100px] flex w-full rounded-md border px-3 py-2 text-base placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    autoComplete="off"
                    spellCheck="false"
                    data-testid="cover-lyrics-input"
                  />
                </div>

                <Button
                  onClick={handleCreateCover}
                  disabled={loading || !coverUrl}
                  className="w-full font-bold uppercase tracking-wider"
                  style={{ backgroundColor: theme.primary, color: '#000' }}
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mic className="w-4 h-4 mr-2" />}
                  Create Cover
                </Button>
              </div>
            </GlassCard>
          </TabsContent>

          {/* Mastering Tab */}
          <TabsContent value="master">
            <GlassCard>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.primary }}>
                <Wand2 className="w-5 h-5" />
                AI Audio Mastering
              </h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Song to Master
                  </label>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => openSongPicker('master')}
                      variant="outline"
                      className="flex-1 justify-start"
                      style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    >
                      <FolderOpen className="w-4 h-4 mr-2" />
                      {masterFileName || 'Select Song'}
                    </Button>
                    {masterFileName && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setMasterUrl(''); setMasterFileName(''); }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  {masterUrl && (
                    <p className="text-xs mt-1 truncate" style={{ color: 'var(--text-muted)' }}>{masterUrl}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Reference Track
                  </label>
                  <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>
                    Choose a professionally mastered track as a reference for the AI to match
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setShowReferencePicker(true)}
                      variant="outline"
                      className="flex-1 justify-start"
                      style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    >
                      <Music className="w-4 h-4 mr-2" />
                      {referenceTrackFileName || 'Select Reference Track'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => referenceFileInputRef.current?.click()}
                      style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    >
                      <Upload className="w-4 h-4" />
                    </Button>
                    <input
                      ref={referenceFileInputRef}
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      onChange={handleReferenceFileUpload}
                    />
                    {referenceTrackFileName && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setReferenceTrackUrl(''); setReferenceTrackFileName(''); }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  {referenceTrackUrl && (
                    <p className="text-xs mt-1 truncate" style={{ color: 'var(--text-muted)' }}>{referenceTrackUrl}</p>
                  )}
                  <div className="mt-2">
                    <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
                      Or paste a URL directly:
                    </label>
                    <StudioInput
                      inputRef={referenceUrlRef}
                      placeholder="https://example.com/reference-track.mp3"
                      defaultValue=""
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Output Format
                  </label>
                  <Select value={outputFormat} onValueChange={setOutputFormat}>
                    <SelectTrigger style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="wav">WAV (Lossless)</SelectItem>
                      <SelectItem value="mp3">MP3</SelectItem>
                      <SelectItem value="flac">FLAC</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  onClick={handleMaster}
                  disabled={loading || !masterUrl}
                  className="w-full font-bold uppercase tracking-wider"
                  style={{ backgroundColor: theme.primary, color: '#000' }}
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                  Master Track
                </Button>
              </div>
            </GlassCard>
          </TabsContent>

          {/* Extend Tab */}
          <TabsContent value="extend">
            <GlassCard>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.primary }}>
                <Maximize2 className="w-5 h-5" />
                Extend Music
              </h2>
              
              {!aiConfig?.extend?.enabled ? (
                <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: 'rgba(239,68,68,0.1)', borderColor: '#ef4444' }}>
                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    <strong>Suno API key required.</strong> Add SUNO_API_KEY in Secrets to enable music extension.
                  </p>
                </div>
              ) : (
                <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: `${theme.secondary}10`, borderColor: theme.secondary }}>
                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    <strong>Suno AI Extension:</strong> Extend your Suno-generated tracks with AI continuation. Select a track from your AI library and choose where to continue from.
                  </p>
                </div>
              )}
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Select Song to Extend</label>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => openSongPicker('extend')}
                      variant="outline"
                      className="flex-1"
                      style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    >
                      <FolderOpen className="w-4 h-4 mr-2" />
                      {extendSongTitle || 'Browse AI Library'}
                    </Button>
                    {extendAudioId && (
                      <Button
                        onClick={() => { setExtendUrl(''); setExtendAudioId(''); setExtendTaskId(''); setExtendSongTitle(''); }}
                        variant="outline"
                        size="icon"
                        style={{ borderColor: 'var(--border-color)', color: 'var(--text-muted)' }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  {(extendTaskId || extendAudioId) && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      <CopyIdBadge label="Task ID" value={extendTaskId} />
                      <CopyIdBadge label="Audio ID" value={extendAudioId} />
                    </div>
                  )}
                  {extendUrl && !extendAudioId && (
                    <p className="text-xs mt-2" style={{ color: '#ef4444' }}>
                      No Suno ID found. Only Suno-generated tracks can be extended.
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Suno Model</label>
                    <Select value={extendModelVersion} onValueChange={setExtendModelVersion}>
                      <SelectTrigger className="w-full" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent style={{ backgroundColor: isDarkMode ? '#000' : '#fff' }}>
                        <SelectItem value="V5_5">V5.5 (Latest & Best)</SelectItem>
                        <SelectItem value="V5">V5 (Superior Expression)</SelectItem>
                        <SelectItem value="V4_5PLUS">V4.5+ (Max 8 min)</SelectItem>
                        <SelectItem value="V4_5">V4.5 (Faster)</SelectItem>
                        <SelectItem value="V4">V4 (Better Vocals)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 flex items-center gap-1" style={{ color: 'var(--text-primary)' }}>
                      <Clock className="w-4 h-4" />
                      Continue From (seconds)
                    </label>
                    <input
                      type="number"
                      value={extendContinueAt}
                      onChange={(e) => setExtendContinueAt(e.target.value)}
                      placeholder="60"
                      min="1"
                      className="flex w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    />
                    <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Time point (in seconds) where extension begins</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Vocal Gender</label>
                    <Select value={extendVocalGender} onValueChange={setExtendVocalGender}>
                      <SelectTrigger style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent style={{ backgroundColor: isDarkMode ? '#000' : '#fff' }}>
                        <SelectItem value="auto">Auto</SelectItem>
                        <SelectItem value="m">Male</SelectItem>
                        <SelectItem value="f">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={extendUseOriginal}
                      onCheckedChange={setExtendUseOriginal}
                    />
                    <label className="text-sm" style={{ color: 'var(--text-primary)' }}>Use Original Parameters</label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={extendInstrumental}
                      onCheckedChange={setExtendInstrumental}
                    />
                    <label className="text-sm" style={{ color: 'var(--text-primary)' }}>Instrumental (no vocals)</label>
                  </div>
                </div>

                {!extendUseOriginal && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Extension Title</label>
                        <input
                          value={extendTitle}
                          onChange={(e) => setExtendTitle(e.target.value.slice(0, extendTitleLimit))}
                          placeholder={extendSongTitle ? `${extendSongTitle} (Extended)` : 'e.g. My Track Extended'}
                          className="flex w-full rounded-md border px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                        />
                        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{extendTitle.length}/{extendTitleLimit}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Music Style</label>
                        <input
                          value={extendStyle}
                          onChange={(e) => setExtendStyle(e.target.value.slice(0, extendStyleLimit))}
                          placeholder="Hip-Hop, Trap, Crunk"
                          className="flex w-full rounded-md border px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                        />
                        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{extendStyle.length}/{extendStyleLimit}</p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                        {extendInstrumental ? 'Extension Prompt (style description)' : 'Extension Lyrics'}
                        <span className="text-xs font-normal ml-2" style={{ color: 'var(--text-muted)' }}>({extendPrompt.length}/{extendPromptLimit})</span>
                      </label>
                      <textarea
                        value={extendPrompt}
                        onChange={(e) => setExtendPrompt(e.target.value.slice(0, extendPromptLimit))}
                        placeholder={extendInstrumental ? "Describe how the extension should sound, e.g. 'Add a bridge section with slower tempo'" : "Write lyrics for the extension, or leave empty for AI-generated lyrics"}
                        className="min-h-[100px] flex w-full rounded-md border px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Negative Tags (Optional)</label>
                      <input
                        value={extendNegativeTags}
                        onChange={(e) => setExtendNegativeTags(e.target.value.slice(0, 200))}
                        placeholder="Styles to avoid, e.g. Heavy Metal, Upbeat Drums"
                        className="flex w-full rounded-md border px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                      />
                    </div>
                  </>
                )}

                <Button
                  onClick={handleExtend}
                  disabled={extendLoading || !extendAudioId || !aiConfig?.extend?.enabled}
                  className="w-full font-bold uppercase tracking-wider"
                  style={{ backgroundColor: theme.primary, color: '#000' }}
                >
                  {extendLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Maximize2 className="w-4 h-4 mr-2" />}
                  Extend Music
                </Button>
              </div>
            </GlassCard>
          </TabsContent>

          {/* Add Instrumental Tab */}
          <TabsContent value="add_instrumental">
            <GlassCard>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.primary }}>
                <Guitar className="w-5 h-5" />
                Add Instrumental
              </h2>
              
              {!aiConfig?.suno?.enabled ? (
                <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: 'rgba(239,68,68,0.1)', borderColor: '#ef4444' }}>
                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    <strong>Suno API key required.</strong> Add SUNO_API_KEY in Secrets to enable this feature.
                  </p>
                </div>
              ) : (
                <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: `${theme.primary}10`, borderColor: theme.primary }}>
                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    <strong>AI Instrumental:</strong> Upload a vocal track and AI will generate a matching instrumental backing track.
                  </p>
                </div>
              )}
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Select Song</label>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => openSongPicker('add_instrumental')}
                      variant="outline"
                      className="flex-1"
                      style={{ borderColor: theme.primary, color: theme.primary }}
                    >
                      <FolderOpen className="w-4 h-4 mr-2" />
                      {addInstrumentalFileName || 'Browse Library / Upload'}
                    </Button>
                    {addInstrumentalFileName && (
                      <Button variant="ghost" onClick={() => { setAddInstrumentalUrl(''); setAddInstrumentalFileName(''); }}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Title</label>
                    <input
                      value={addInstrumentalTitle}
                      onChange={(e) => setAddInstrumentalTitle(e.target.value)}
                      placeholder="e.g. Summer Beat Instrumental"
                      className="flex w-full rounded-md border px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Suno Model</label>
                    <Select value={addInstrumentalModel} onValueChange={setAddInstrumentalModel}>
                      <SelectTrigger style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent style={{ backgroundColor: isDarkMode ? '#000' : '#fff' }}>
                        <SelectItem value="V5_5">V5.5 (Latest & Best)</SelectItem>
                        <SelectItem value="V5">V5 (Superior Expression)</SelectItem>
                        <SelectItem value="V4_5PLUS">V4.5+ (Max 8 min)</SelectItem>
                        <SelectItem value="V4_5">V4.5 (Faster)</SelectItem>
                        <SelectItem value="V4">V4 (Better Vocals)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Style Tags (Optional)</label>
                  <input
                    value={addInstrumentalStyle}
                    onChange={(e) => setAddInstrumentalStyle(e.target.value)}
                    placeholder="e.g. Lo-fi, jazzy piano, smooth drums"
                    className="flex w-full rounded-md border px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    maxLength={200}
                  />
                </div>

                <Button
                  onClick={handleAddInstrumental}
                  disabled={addInstrumentalLoading || !addInstrumentalUrl}
                  className="w-full font-bold uppercase tracking-wider"
                  style={{ backgroundColor: theme.primary, color: '#000' }}
                >
                  {addInstrumentalLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Guitar className="w-4 h-4 mr-2" />}
                  Add Instrumental
                </Button>
              </div>
            </GlassCard>
          </TabsContent>

          {/* Add Vocals Tab */}
          <TabsContent value="add_vocals">
            <GlassCard>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.primary }}>
                <MicVocal className="w-5 h-5" />
                Add Vocals
              </h2>
              
              {!aiConfig?.suno?.enabled ? (
                <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: 'rgba(239,68,68,0.1)', borderColor: '#ef4444' }}>
                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    <strong>Suno API key required.</strong> Add SUNO_API_KEY in Secrets to enable this feature.
                  </p>
                </div>
              ) : (
                <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: `${theme.secondary}10`, borderColor: theme.secondary }}>
                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    <strong>AI Vocals:</strong> Upload an instrumental and AI will generate matching vocals on top of it.
                  </p>
                </div>
              )}
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Select Instrumental</label>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => openSongPicker('add_vocals')}
                      variant="outline"
                      className="flex-1"
                      style={{ borderColor: theme.primary, color: theme.primary }}
                    >
                      <FolderOpen className="w-4 h-4 mr-2" />
                      {addVocalsFileName || 'Browse Library / Upload'}
                    </Button>
                    {addVocalsFileName && (
                      <Button variant="ghost" onClick={() => { setAddVocalsUrl(''); setAddVocalsFileName(''); }}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Vocal Lyrics (leave empty for AI-generated)</label>
                  <textarea
                    value={addVocalsPrompt}
                    onChange={(e) => setAddVocalsPrompt(e.target.value)}
                    placeholder="Write the lyrics you want sung, or leave empty to let AI generate lyrics"
                    className="min-h-[80px] flex w-full rounded-md border px-3 py-2 text-base placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    maxLength={500}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Vocal Gender</label>
                    <Select value={addVocalsGender} onValueChange={setAddVocalsGender}>
                      <SelectTrigger style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent style={{ backgroundColor: isDarkMode ? '#000' : '#fff' }}>
                        <SelectItem value="m">Male</SelectItem>
                        <SelectItem value="f">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Suno Model</label>
                    <Select value={addVocalsModel} onValueChange={setAddVocalsModel}>
                      <SelectTrigger style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent style={{ backgroundColor: isDarkMode ? '#000' : '#fff' }}>
                        <SelectItem value="V5_5">V5.5 (Latest & Best)</SelectItem>
                        <SelectItem value="V5">V5 (Superior Expression)</SelectItem>
                        <SelectItem value="V4_5PLUS">V4.5+ (Max 8 min)</SelectItem>
                        <SelectItem value="V4_5">V4.5 (Faster)</SelectItem>
                        <SelectItem value="V4">V4 (Better Vocals)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Style Tags</label>
                    <input
                      value={addVocalsStyle}
                      onChange={(e) => setAddVocalsStyle(e.target.value)}
                      placeholder="e.g. Auto-tune, melodic"
                      className="flex w-full rounded-md border px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                      maxLength={200}
                    />
                  </div>
                </div>

                <Button
                  onClick={handleAddVocals}
                  disabled={addVocalsLoading || !addVocalsUrl}
                  className="w-full font-bold uppercase tracking-wider"
                  style={{ backgroundColor: theme.primary, color: '#000' }}
                >
                  {addVocalsLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MicVocal className="w-4 h-4 mr-2" />}
                  Add Vocals
                </Button>
              </div>
            </GlassCard>
          </TabsContent>

          {/* Replace Section Tab */}
          <TabsContent value="replace_section">
            <GlassCard>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.primary }}>
                <Replace className="w-5 h-5" />
                Replace Section
              </h2>
              
              {!aiConfig?.suno?.enabled ? (
                <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: 'rgba(239,68,68,0.1)', borderColor: '#ef4444' }}>
                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    <strong>Suno API key required.</strong> Add SUNO_API_KEY in Secrets to enable this feature.
                  </p>
                </div>
              ) : (
                <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: `${theme.primary}10`, borderColor: theme.primary }}>
                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    <strong>Section Replace:</strong> Select a Suno-generated track and replace a specific time range with new AI-generated content. Only works with tracks that have a Suno Audio ID.
                  </p>
                </div>
              )}
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Select Suno Track</label>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => openSongPicker('replace_section')}
                      variant="outline"
                      className="flex-1"
                      style={{ borderColor: theme.primary, color: theme.primary }}
                    >
                      <FolderOpen className="w-4 h-4 mr-2" />
                      {replaceSectionTitle || 'Browse AI Library'}
                    </Button>
                    {replaceSectionAudioId && (
                      <Button variant="ghost" onClick={() => { setReplaceSectionAudioId(''); setReplaceSectionTaskId(''); setReplaceSectionTitle(''); }}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  {(replaceSectionTaskId || replaceSectionAudioId) && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      <CopyIdBadge label="Task ID" value={replaceSectionTaskId} />
                      <CopyIdBadge label="Audio ID" value={replaceSectionAudioId} />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 flex items-center gap-1" style={{ color: 'var(--text-primary)' }}>
                      <Clock className="w-4 h-4" />
                      Start (seconds)
                    </label>
                    <input
                      type="number"
                      value={replaceSectionStart}
                      onChange={(e) => setReplaceSectionStart(e.target.value)}
                      placeholder="0"
                      min="0"
                      className="flex w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 flex items-center gap-1" style={{ color: 'var(--text-primary)' }}>
                      <Clock className="w-4 h-4" />
                      End (seconds)
                    </label>
                    <input
                      type="number"
                      value={replaceSectionEnd}
                      onChange={(e) => setReplaceSectionEnd(e.target.value)}
                      placeholder="30"
                      min="1"
                      className="flex w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Suno Model</label>
                    <Select value={replaceSectionModel} onValueChange={setReplaceSectionModel}>
                      <SelectTrigger style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent style={{ backgroundColor: isDarkMode ? '#000' : '#fff' }}>
                        <SelectItem value="V5_5">V5.5 (Latest & Best)</SelectItem>
                        <SelectItem value="V5">V5 (Superior Expression)</SelectItem>
                        <SelectItem value="V4_5PLUS">V4.5+ (Max 8 min)</SelectItem>
                        <SelectItem value="V4_5">V4.5 (Faster)</SelectItem>
                        <SelectItem value="V4">V4 (Better Vocals)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Replacement Lyrics (leave empty for AI-generated)</label>
                  <textarea
                    value={replaceSectionPrompt}
                    onChange={(e) => setReplaceSectionPrompt(e.target.value)}
                    placeholder="Write lyrics for the replacement section, or leave empty to let AI generate lyrics"
                    className="min-h-[80px] flex w-full rounded-md border px-3 py-2 text-base placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    maxLength={500}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Style Tags (Optional)</label>
                  <input
                    value={replaceSectionStyle}
                    onChange={(e) => setReplaceSectionStyle(e.target.value)}
                    placeholder="e.g. chill, acoustic, dreamy"
                    className="flex w-full rounded-md border px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    maxLength={200}
                  />
                </div>

                <Button
                  onClick={handleReplaceSection}
                  disabled={replaceSectionLoading || !replaceSectionAudioId}
                  className="w-full font-bold uppercase tracking-wider"
                  style={{ backgroundColor: theme.primary, color: '#000' }}
                >
                  {replaceSectionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Replace className="w-4 h-4 mr-2" />}
                  Replace Section
                </Button>
              </div>
            </GlassCard>
          </TabsContent>

          {/* Mashup Tab */}
          <TabsContent value="mashup">
            <GlassCard>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.primary }}>
                <Shuffle className="w-5 h-5" />
                AI Mashup
              </h2>
              
              {!aiConfig?.suno?.enabled ? (
                <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: 'rgba(239,68,68,0.1)', borderColor: '#ef4444' }}>
                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    <strong>Suno API key required.</strong> Add SUNO_API_KEY in Secrets to enable mashups.
                  </p>
                </div>
              ) : (
                <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: `${theme.secondary}10`, borderColor: theme.secondary }}>
                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    <strong>AI Mashup:</strong> Combine 2 or more tracks into a single cohesive mashup. Select tracks from your library or upload files.
                  </p>
                </div>
              )}
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Selected Tracks ({mashupTracks.length}/10)
                  </label>
                  {mashupTracks.length > 0 && (
                    <div className="space-y-1 mb-3">
                      {mashupTracks.map((track, i) => (
                        <div key={i} className="flex items-center justify-between p-2 rounded bg-white/5">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold px-2 py-0.5 rounded" style={{ backgroundColor: `${theme.primary}20`, color: theme.primary }}>
                              {i + 1}
                            </span>
                            <span className="text-sm truncate" style={{ color: 'var(--text-primary)' }}>{track.name}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setMashupTracks(prev => prev.filter((_, idx) => idx !== i))}
                            className="text-red-400 hover:text-red-300"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <Button
                    onClick={() => openSongPicker('mashup')}
                    variant="outline"
                    className="w-full"
                    style={{ borderColor: theme.primary, color: theme.primary }}
                    disabled={mashupTracks.length >= 10}
                  >
                    <FolderOpen className="w-4 h-4 mr-2" />
                    Add Track from Library / Upload
                  </Button>
                  {mashupTracks.length < 2 && (
                    <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                      Add at least 2 tracks to create a mashup
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Mashup Title</label>
                    <input
                      value={mashupTitle}
                      onChange={(e) => setMashupTitle(e.target.value)}
                      placeholder="e.g. Summer Vibes Mashup"
                      className="flex w-full rounded-md border px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Suno Model</label>
                    <Select value={mashupModel} onValueChange={setMashupModel}>
                      <SelectTrigger style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent style={{ backgroundColor: isDarkMode ? '#000' : '#fff' }}>
                        <SelectItem value="V5_5">V5.5 (Latest & Best)</SelectItem>
                        <SelectItem value="V5">V5 (Superior Expression)</SelectItem>
                        <SelectItem value="V4_5PLUS">V4.5+ (Max 8 min)</SelectItem>
                        <SelectItem value="V4_5">V4.5 (Faster)</SelectItem>
                        <SelectItem value="V4">V4 (Better Vocals)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center gap-6 p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Switch
                      checked={!mashupCustomMode}
                      onCheckedChange={(v) => setMashupCustomMode(!v)}
                    />
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Use Original (no prompt/style)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Switch
                      checked={mashupInstrumental}
                      onCheckedChange={setMashupInstrumental}
                    />
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Instrumental Only</span>
                  </label>
                </div>
                {!mashupInstrumental && mashupCustomMode && (
                  <p className="text-xs px-1" style={{ color: '#f59e0b' }}>
                    When "Instrumental Only" is off, text in the Lyrics field below will be sung as vocals. Leave it empty to let AI decide.
                  </p>
                )}

                {mashupCustomMode && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                        {mashupInstrumental ? 'Mashup Prompt (Optional)' : 'Lyrics (will be sung)'}
                      </label>
                      <textarea
                        value={mashupPrompt}
                        onChange={(e) => setMashupPrompt(e.target.value)}
                        placeholder={mashupInstrumental
                          ? "Describe the vibe, e.g. 'Blend the beats smoothly with a hip-hop feel'"
                          : "Enter lyrics to be sung, or leave empty to use original vocals"
                        }
                        className="min-h-[80px] flex w-full rounded-md border px-3 py-2 text-base placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                        maxLength={3000}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Style Tags</label>
                        <input
                          value={mashupStyle}
                          onChange={(e) => setMashupStyle(e.target.value)}
                          placeholder="e.g. Hip-Hop, Trap, Crunk"
                          className="flex w-full rounded-md border px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                          maxLength={200}
                        />
                      </div>
                      {!mashupInstrumental && (
                        <div>
                          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Vocal Gender</label>
                          <Select value={mashupVocalGender} onValueChange={setMashupVocalGender}>
                            <SelectTrigger style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                              <SelectValue placeholder="Auto" />
                            </SelectTrigger>
                            <SelectContent style={{ backgroundColor: isDarkMode ? '#000' : '#fff' }}>
                              <SelectItem value="">Auto</SelectItem>
                              <SelectItem value="m">Male</SelectItem>
                              <SelectItem value="f">Female</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>

                    <div className="space-y-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                      <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Mixing Weights</p>
                      <div>
                        <div className="flex justify-between mb-1">
                          <label className="text-xs" style={{ color: 'var(--text-primary)' }}>Style Weight</label>
                          <span className="text-xs font-mono" style={{ color: theme.primary }}>{parseFloat(mashupStyleWeight).toFixed(2)}</span>
                        </div>
                        <input type="range" min="0" max="1" step="0.01" value={mashupStyleWeight} onChange={(e) => setMashupStyleWeight(e.target.value)} className="w-full h-1.5 rounded-lg appearance-none cursor-pointer" style={{ accentColor: theme.primary }} />
                      </div>
                      <div>
                        <div className="flex justify-between mb-1">
                          <label className="text-xs" style={{ color: 'var(--text-primary)' }}>Weirdness</label>
                          <span className="text-xs font-mono" style={{ color: theme.primary }}>{parseFloat(mashupWeirdness).toFixed(2)}</span>
                        </div>
                        <input type="range" min="0" max="1" step="0.01" value={mashupWeirdness} onChange={(e) => setMashupWeirdness(e.target.value)} className="w-full h-1.5 rounded-lg appearance-none cursor-pointer" style={{ accentColor: theme.primary }} />
                      </div>
                      <div>
                        <div className="flex justify-between mb-1">
                          <label className="text-xs" style={{ color: 'var(--text-primary)' }}>Audio Weight</label>
                          <span className="text-xs font-mono" style={{ color: theme.primary }}>{parseFloat(mashupAudioWeight).toFixed(2)}</span>
                        </div>
                        <input type="range" min="0" max="1" step="0.01" value={mashupAudioWeight} onChange={(e) => setMashupAudioWeight(e.target.value)} className="w-full h-1.5 rounded-lg appearance-none cursor-pointer" style={{ accentColor: theme.primary }} />
                      </div>
                    </div>
                  </>
                )}

                {!mashupCustomMode && (
                  <div className="p-3 rounded-lg border" style={{ backgroundColor: `${theme.secondary}10`, borderColor: theme.secondary }}>
                    <p className="text-xs" style={{ color: 'var(--text-primary)' }}>
                      "Use Original" is ON — no prompt, style, or lyrics will be sent. The AI will automatically blend the tracks using their existing sound and characteristics.
                    </p>
                  </div>
                )}

                <Button
                  onClick={handleMashup}
                  disabled={mashupLoading || mashupTracks.length < 2}
                  className="w-full font-bold uppercase tracking-wider"
                  style={{ backgroundColor: theme.primary, color: '#000' }}
                >
                  {mashupLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shuffle className="w-4 h-4 mr-2" />}
                  Create Mashup ({mashupTracks.length} tracks)
                </Button>
              </div>
            </GlassCard>
          </TabsContent>

          {/* WAV Convert Tab */}
          <TabsContent value="wav_convert">
            <GlassCard>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.primary }}>
                <FileAudio className="w-5 h-5" />
                WAV Convert
              </h2>
              
              {!aiConfig?.suno?.enabled ? (
                <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: 'rgba(239,68,68,0.1)', borderColor: '#ef4444' }}>
                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    <strong>Suno API key required.</strong> Add SUNO_API_KEY in Secrets to enable WAV conversion.
                  </p>
                </div>
              ) : (
                <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: `${theme.primary}10`, borderColor: theme.primary }}>
                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    <strong>WAV Conversion:</strong> Convert a completed Suno generation to lossless WAV format. You need the Task ID and Audio ID from a completed Suno task.
                    Find these values in your Library tab under Recent Tasks.
                  </p>
                </div>
              )}
              
              <div className="space-y-4">
                <Button
                  onClick={() => openSongPicker('wav_convert')}
                  variant="outline"
                  className="w-full mb-2"
                  style={{ borderColor: theme.primary, color: theme.primary }}
                >
                  <Music className="w-4 h-4 mr-2" /> Pick from AI Library
                </Button>
                {(wavTaskId || wavAudioId) && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    <CopyIdBadge label="Task ID" value={wavTaskId} />
                    <CopyIdBadge label="Audio ID" value={wavAudioId} />
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Suno Task ID</label>
                    <input
                      value={wavTaskId}
                      onChange={(e) => setWavTaskId(e.target.value)}
                      placeholder="Auto-filled from library or paste manually"
                      className="flex w-full rounded-md border px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Suno Audio ID</label>
                    <input
                      value={wavAudioId}
                      onChange={(e) => setWavAudioId(e.target.value)}
                      placeholder="Auto-filled from library or paste manually"
                      className="flex w-full rounded-md border px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    />
                  </div>
                </div>

                <Button
                  onClick={handleWavConvert}
                  disabled={wavLoading || !wavTaskId.trim() || !wavAudioId.trim()}
                  className="w-full font-bold uppercase tracking-wider"
                  style={{ backgroundColor: theme.primary, color: '#000' }}
                >
                  {wavLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileAudio className="w-4 h-4 mr-2" />}
                  Convert to WAV
                </Button>

                {wavResult && (
                  <div className="p-4 rounded-lg border" style={{ backgroundColor: `${theme.primary}10`, borderColor: theme.primary }}>
                    <p className="text-sm font-medium mb-2" style={{ color: theme.primary }}>WAV Ready!</p>
                    <button
                      onClick={() => downloadFile(wavResult, `${wavTaskId || 'track'}.wav`)}
                      className="flex items-center gap-2 text-sm underline"
                      style={{ color: theme.primary }}
                    >
                      <Download className="w-4 h-4" />
                      Download WAV File
                    </button>
                  </div>
                )}
              </div>
            </GlassCard>
          </TabsContent>

          {/* Suno Lyrics Tab */}
          <TabsContent value="suno_lyrics">
            <GlassCard>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.primary }}>
                <FileText className="w-5 h-5" />
                Suno Lyrics Generator
              </h2>
              
              {!aiConfig?.suno?.enabled ? (
                <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: 'rgba(239,68,68,0.1)', borderColor: '#ef4444' }}>
                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    <strong>Suno API key required.</strong> Add SUNO_API_KEY in Secrets to enable lyrics generation.
                  </p>
                </div>
              ) : (
                <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: `${theme.secondary}10`, borderColor: theme.secondary }}>
                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    <strong>Suno Lyrics API:</strong> Generate song lyrics using Suno's dedicated lyrics API. Different from the MusicGPT lyrics generator.
                  </p>
                </div>
              )}
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Lyrics Prompt</label>
                  <textarea
                    value={sunoLyricsPrompt}
                    onChange={(e) => setSunoLyricsPrompt(e.target.value)}
                    placeholder="Describe the lyrics you want, e.g. 'A trap song about grinding and hustling in the streets, with a catchy hook about never giving up'"
                    className="min-h-[120px] flex w-full rounded-md border px-3 py-2 text-base placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    maxLength={200}
                  />
                  <div className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{sunoLyricsPrompt.length}/200</div>
                </div>

                <Button
                  onClick={handleSunoLyrics}
                  disabled={sunoLyricsLoading || !sunoLyricsPrompt.trim()}
                  className="w-full font-bold uppercase tracking-wider"
                  style={{ backgroundColor: theme.primary, color: '#000' }}
                >
                  {sunoLyricsLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                  Generate Lyrics
                </Button>

                {sunoLyricsResult && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium" style={{ color: theme.primary }}>
                        Generated Lyrics
                        {sunoLyricsVariations.length > 1 && (
                          <span className="ml-2 text-xs font-normal" style={{ color: 'var(--text-secondary)' }}>
                            {sunoLyricsVariations[sunoLyricsVariationIndex]?.title && `"${sunoLyricsVariations[sunoLyricsVariationIndex].title}"`}
                          </span>
                        )}
                      </label>
                      <div className="flex items-center gap-2">
                        {sunoLyricsVariations.length > 1 && (
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={sunoLyricsVariationIndex === 0}
                              onClick={() => {
                                const newIndex = sunoLyricsVariationIndex - 1;
                                setSunoLyricsVariationIndex(newIndex);
                                setSunoLyricsResult(sunoLyricsVariations[newIndex].text);
                              }}
                              style={{ borderColor: theme.primary, color: theme.primary, padding: '0 8px' }}
                            >
                              &larr;
                            </Button>
                            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                              {sunoLyricsVariationIndex + 1}/{sunoLyricsVariations.length}
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={sunoLyricsVariationIndex === sunoLyricsVariations.length - 1}
                              onClick={() => {
                                const newIndex = sunoLyricsVariationIndex + 1;
                                setSunoLyricsVariationIndex(newIndex);
                                setSunoLyricsResult(sunoLyricsVariations[newIndex].text);
                              }}
                              style={{ borderColor: theme.primary, color: theme.primary, padding: '0 8px' }}
                            >
                              &rarr;
                            </Button>
                          </div>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(sunoLyricsResult);
                            toast.success('Lyrics copied to clipboard!');
                          }}
                          style={{ borderColor: theme.primary, color: theme.primary }}
                        >
                          Copy
                        </Button>
                      </div>
                    </div>
                    <div
                      className="p-4 rounded-lg whitespace-pre-wrap text-sm max-h-[400px] overflow-y-auto"
                      style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                    >
                      {sunoLyricsResult}
                    </div>
                  </div>
                )}
              </div>
            </GlassCard>
          </TabsContent>

          <TabsContent value="boost_style">
            <GlassCard>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.primary }}>
                <Zap className="w-5 h-5" /> Boost Style
              </h2>
              <p className="text-sm text-gray-400 mb-4">Enter a style description and get an enhanced, AI-optimized version to use in your prompts.</p>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-300 mb-1 block">Style Description</label>
                  <textarea
                    value={boostStyleContent}
                    onChange={(e) => setBoostStyleContent(e.target.value)}
                    placeholder="e.g. Upbeat 80s synthwave with female vocals, retro drums, neon vibes..."
                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder-gray-500 min-h-[100px] resize-y"
                    maxLength={1000}
                  />
                  <div className="text-xs text-gray-500 mt-1">{boostStyleContent.length}/1000</div>
                </div>

                <Button
                  onClick={handleBoostStyle}
                  disabled={boostStyleLoading || !boostStyleContent.trim()}
                  className="w-full font-bold uppercase tracking-wider"
                  style={{ background: boostStyleLoading ? undefined : `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})` }}
                >
                  {boostStyleLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Boosting...</> : <><Zap className="w-4 h-4 mr-2" /> Boost Style</>}
                </Button>

                {boostStyleResult && (
                  <div className="mt-4 p-4 bg-white/5 border border-white/10 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="text-sm font-semibold text-gray-300">Boosted Style:</h3>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { navigator.clipboard.writeText(boostStyleResult); toast.success('Copied to clipboard!'); }}
                        className="text-xs"
                      >
                        Copy
                      </Button>
                    </div>
                    <p className="text-white text-sm whitespace-pre-wrap">{boostStyleResult}</p>
                  </div>
                )}
              </div>
            </GlassCard>
          </TabsContent>

          <TabsContent value="persona">
            <GlassCard>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.primary }}>
                <UserCircle className="w-5 h-5" /> Create Persona
              </h2>
              <p className="text-sm text-gray-400 mb-4">Create a reusable voice persona from a Suno-generated track. Use the returned Persona ID in future generations.</p>
              <div className="space-y-4">
                <Button
                  onClick={() => openSongPicker('persona')}
                  variant="outline"
                  className="w-full mb-2"
                  style={{ borderColor: theme.primary, color: theme.primary }}
                >
                  <Music className="w-4 h-4 mr-2" /> Pick from AI Library
                </Button>
                {(personaTaskId || personaAudioId) && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    <CopyIdBadge label="Task ID" value={personaTaskId} />
                    <CopyIdBadge label="Audio ID" value={personaAudioId} />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm mb-1 block" style={{ color: 'var(--text-primary)' }}>Task ID</label>
                    <input
                      type="text"
                      value={personaTaskId}
                      onChange={(e) => setPersonaTaskId(e.target.value)}
                      placeholder="Auto-filled from library or paste manually"
                      className="flex w-full rounded-md border px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    />
                  </div>
                  <div>
                    <label className="text-sm mb-1 block" style={{ color: 'var(--text-primary)' }}>Audio ID</label>
                    <input
                      type="text"
                      value={personaAudioId}
                      onChange={(e) => setPersonaAudioId(e.target.value)}
                      placeholder="Auto-filled from library or paste manually"
                      className="flex w-full rounded-md border px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-300 mb-1 block">Persona Name <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    value={personaName}
                    onChange={(e) => setPersonaName(e.target.value)}
                    placeholder="e.g. Neon Dreams Singer"
                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder-gray-500"
                    maxLength={100}
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-300 mb-1 block">Description <span className="text-red-400">*</span></label>
                  <textarea
                    value={personaDescription}
                    onChange={(e) => setPersonaDescription(e.target.value)}
                    placeholder="e.g. A modern electronic pop singer, skilled in dynamic rhythms and synthesizer tones"
                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder-gray-500 min-h-[80px] resize-y"
                    maxLength={500}
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-300 mb-1 block">Style Tag</label>
                  <input
                    type="text"
                    value={personaStyle}
                    onChange={(e) => setPersonaStyle(e.target.value)}
                    placeholder="e.g. Electronic Pop, Jazz Trio, Hip-Hop"
                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder-gray-500"
                    maxLength={200}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-300 mb-1 block">Vocal Start (sec)</label>
                    <input
                      type="number"
                      value={personaVocalStart}
                      onChange={(e) => setPersonaVocalStart(e.target.value)}
                      min={0}
                      step={0.5}
                      placeholder="0"
                      className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder-gray-500"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-300 mb-1 block">Vocal End (sec)</label>
                    <input
                      type="number"
                      value={personaVocalEnd}
                      onChange={(e) => setPersonaVocalEnd(e.target.value)}
                      min={0}
                      step={0.5}
                      placeholder="30"
                      className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder-gray-500"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500">Vocal segment for analysis must be 10–30 seconds long. Default: 0–30s.</p>

                <Button
                  onClick={handlePersona}
                  disabled={personaLoading || !personaTaskId.trim() || !personaAudioId.trim() || !personaName.trim() || !personaDescription.trim()}
                  className="w-full font-bold uppercase tracking-wider"
                  style={{ background: personaLoading ? undefined : `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})` }}
                >
                  {personaLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Creating Persona...</> : <><UserCircle className="w-4 h-4 mr-2" /> Create Persona</>}
                </Button>

                {personaResult && (
                  <div className="mt-4 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <h3 className="text-sm font-semibold text-green-400 mb-2">Persona Created!</h3>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-300 text-sm">Persona ID:</span>
                      <code className="bg-white/10 px-2 py-1 rounded text-white text-sm font-mono">{personaResult}</code>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { navigator.clipboard.writeText(personaResult); toast.success('Persona ID copied!'); }}
                        className="text-xs"
                      >
                        Copy
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Use this ID in future generations by adding it to your prompt settings.</p>
                  </div>
                )}
              </div>
            </GlassCard>
          </TabsContent>

                  {/* Library Tab */}
          <TabsContent value="library">
            <GlassCard>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.primary }}>
                <FolderOpen className="w-5 h-5" />
                AI Generated Library
                <span className="text-xs font-normal ml-auto" style={{ color: 'var(--text-muted)' }}>Shared between all owners</span>
              </h2>
              
              <ScrollArea className="h-[400px]">
                {aiLibrary.length > 0 ? (
                  <div className="space-y-2">
                    {aiLibrary.map((song) => {
                      const songIsPlaying = isCurrentlyPlaying(song);
                      return (
                        <SongContextMenu key={song.id} song={{ ...song, source: 'ai' }} onDelete={() => deleteAiSong(song.id)} onRefresh={fetchSongLibraries}>
                          <div 
                            className={`flex items-center justify-between p-3 rounded-lg group transition ${songIsPlaying ? 'bg-white/15 ring-1' : 'bg-white/5 hover:bg-white/10'}`}
                            style={songIsPlaying ? { ringColor: theme.primary } : {}}
                          >
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => {
                                  if (songIsPlaying) {
                                    togglePlay();
                                  } else {
                                    playTrack({
                                      id: song.id,
                                      title: song.title,
                                      artist: song.created_by || 'BeatLab52',
                                      audio_url: song.audio_url,
                                      album_cover: song.album_cover
                                    });
                                  }
                                }}
                                className="p-2 rounded-full transition relative"
                                style={{ backgroundColor: `${theme.primary}${songIsPlaying ? '40' : '20'}`, color: theme.primary }}
                                title={songIsPlaying ? 'Pause' : 'Play'}
                                data-testid={`play-btn-${song.id}`}
                              >
                                {songIsPlaying ? (
                                  <AnimatedEqualizer color={theme.primary} />
                                ) : (
                                  <Play className="w-5 h-5" fill="currentColor" />
                                )}
                              </button>
                              {song.album_cover ? (
                                <img src={song.album_cover} alt={song.title} className="w-12 h-12 rounded object-cover" />
                              ) : (
                                <div className="w-12 h-12 rounded flex items-center justify-center" style={{ backgroundColor: `${theme.primary}10` }}>
                                  <Sparkles className="w-5 h-5" style={{ color: theme.primary }} />
                                </div>
                              )}
                              <div>
                                <div className="font-bold flex items-center gap-2">
                                  {song.title}
                                  {songIsPlaying && (
                                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${theme.primary}30`, color: theme.primary }}>
                                      Now Playing
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                  Created by {song.created_by || 'Unknown'}
                                  {song.music_style && ` • ${song.music_style}`}
                                </div>
                                {(() => { const ids = resolveAllIds(song); return (ids.taskId || ids.audioId) ? (
                                  <div className="flex gap-2 mt-1 flex-wrap">
                                    {ids.taskId && (
                                      <span onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(ids.taskId).catch(() => {}); toast.success('Task ID copied'); }} className="text-xs px-1.5 py-0.5 rounded font-mono flex items-center gap-1 cursor-pointer hover:brightness-150" style={{ backgroundColor: `${theme.primary}15`, color: theme.primary }}>
                                        Task: {ids.taskId.slice(0, 16)}... <Copy className="w-3 h-3" />
                                      </span>
                                    )}
                                    {ids.audioId && (
                                      <span onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(ids.audioId).catch(() => {}); toast.success('Audio ID copied'); }} className="text-xs px-1.5 py-0.5 rounded font-mono flex items-center gap-1 cursor-pointer hover:brightness-150" style={{ backgroundColor: `${theme.secondary || theme.primary}15`, color: theme.secondary || theme.primary }}>
                                        Audio: {ids.audioId.slice(0, 16)}... <Copy className="w-3 h-3" />
                                      </span>
                                    )}
                                  </div>
                                ) : null; })()}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {song.audio_url && (
                                <Button size="sm" variant="ghost" onClick={() => downloadFile(song.audio_url, `${song.title || 'track'}.mp3`)} className="opacity-0 group-hover:opacity-100 transition">
                                  <Download className="w-4 h-4" />
                                </Button>
                              )}
                              {isOwner && (
                                <Button size="sm" variant="ghost" onClick={() => deleteAiSong(song.id)} className="text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </SongContextMenu>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center py-8" style={{ color: 'var(--text-muted)' }}>No AI-generated songs yet. Start creating!</p>
                )}
              </ScrollArea>

              {/* Recent Tasks */}
              <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--border-color)' }}>
                <h3 className="font-bold mb-3" style={{ color: theme.primary }}>Recent Tasks</h3>
                <ScrollArea className="h-[200px]">
                  {tasks.length > 0 ? (
                    <div className="space-y-2">
                      {tasks.map((task) => (
                        <div key={task.id} className="flex items-center justify-between p-2 rounded bg-white/5 text-sm">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${task.status === 'completed' ? 'bg-green-500' : task.status === 'failed' ? 'bg-red-500' : 'bg-yellow-500'}`} />
                            <span>{task.type}</span>
                            <span style={{ color: 'var(--text-muted)' }}>by {task.username || 'Unknown'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{task.status}</span>
                            {isOwner && (
                              <button onClick={() => deleteTask(task.id)} className="text-red-400 hover:text-red-300">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center py-4" style={{ color: 'var(--text-muted)' }}>No tasks yet</p>
                  )}
                </ScrollArea>
              </div>
            </GlassCard>
          </TabsContent>

          {/* Sounds Tab */}
          <TabsContent value="sounds">
            <GlassCard>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.primary }}>
                <AudioWaveform className="w-5 h-5" />
                Sound Effects Generator
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                Generate short sound effects, ambient textures, and audio clips using AI.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Sound Description</label>
                  <StudioTextarea
                    placeholder="Describe the sound (e.g., cinematic boom, rain on glass, vinyl scratch, laser beam)..."
                    inputRef={el => { if (el) el._soundsPrompt = true; }}
                    id="sounds-prompt"
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Model</label>
                    <Select defaultValue="V5" onValueChange={v => { document.getElementById('sounds-model-val').value = v; }}>
                      <SelectTrigger className="w-full" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent style={{ backgroundColor: isDarkMode ? '#000' : '#fff' }}>
                        <SelectItem value="V5_5">V5.5 (Latest)</SelectItem>
                        <SelectItem value="V5">V5</SelectItem>
                      </SelectContent>
                    </Select>
                    <input type="hidden" id="sounds-model-val" defaultValue="V5" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Key</label>
                    <Select defaultValue="Any" onValueChange={v => { document.getElementById('sounds-key-val').value = v; }}>
                      <SelectTrigger className="w-full" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent style={{ backgroundColor: isDarkMode ? '#000' : '#fff' }}>
                        <SelectItem value="Any">Any</SelectItem>
                        {['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B', 'Cm', 'C#m', 'Dm', 'D#m', 'Em', 'Fm', 'F#m', 'Gm', 'G#m', 'Am', 'A#m', 'Bm'].map(k => (
                          <SelectItem key={k} value={k}>{k}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <input type="hidden" id="sounds-key-val" defaultValue="Any" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>BPM (Tempo)</label>
                    <StudioInput
                      type="number"
                      id="sounds-tempo"
                      placeholder="e.g. 120 (optional)"
                      min="1"
                      max="300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Options</label>
                    <div className="flex items-center gap-4 h-10">
                      <label className="flex items-center gap-1.5 text-sm cursor-pointer" style={{ color: 'var(--text-primary)' }}>
                        <input type="checkbox" id="sounds-loop" className="rounded" />
                        Loop
                      </label>
                      <label className="flex items-center gap-1.5 text-sm cursor-pointer" style={{ color: 'var(--text-primary)' }}>
                        <input type="checkbox" id="sounds-grab-lyrics" className="rounded" />
                        Grab Lyrics
                      </label>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={async () => {
                    const promptEl = document.getElementById('sounds-prompt');
                    const prompt = promptEl?.value?.trim();
                    if (!prompt) { toast.error('Please describe the sound you want to generate'); return; }
                    const soundPayload = {
                      prompt,
                      model_version: document.getElementById('sounds-model-val')?.value || 'V5',
                    };
                    const tempo = parseInt(document.getElementById('sounds-tempo')?.value);
                    if (tempo && tempo >= 1 && tempo <= 300) soundPayload.sound_tempo = tempo;
                    const key = document.getElementById('sounds-key-val')?.value;
                    if (key && key !== 'Any') soundPayload.sound_key = key;
                    if (document.getElementById('sounds-loop')?.checked) soundPayload.sound_loop = true;
                    if (document.getElementById('sounds-grab-lyrics')?.checked) soundPayload.grab_lyrics = true;
                    try {
                      setIsGenerating(true);
                      const res = await axios.post(`${API}/ai/sounds`, soundPayload, { headers: { Authorization: `Bearer ${token}` } });
                      toast.success(res.data.message || 'Sound generation started!');
                      refreshTasks();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Sound generation failed');
                    } finally {
                      setIsGenerating(false);
                    }
                  }}
                  disabled={isGenerating}
                  className="w-full"
                  style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})` }}
                >
                  {isGenerating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <AudioWaveform className="w-4 h-4 mr-2" />}
                  Generate Sound
                </Button>
              </div>
            </GlassCard>
          </TabsContent>

          {/* Upload Extend Tab */}
          <TabsContent value="upload_extend">
            <GlassCard>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.primary }}>
                <FastForward className="w-5 h-5" />
                Upload & Extend
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                Upload any audio file and extend it with AI-generated continuation. Unlike regular Extend, this works with any audio — not just AI-generated tracks.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Audio Source</label>
                  <div className="flex gap-2">
                    <StudioInput
                      placeholder="Paste audio URL or select from library..."
                      id="upload-extend-url"
                      className="flex-1"
                    />
                    <Button variant="outline" onClick={() => {
                      fetchSongLibraries();
                      setSongPickerTarget('upload_extend');
                      setShowSongPicker(true);
                    }} style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                      <FolderOpen className="w-4 h-4 mr-1" /> Library
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Continue At (seconds)</label>
                    <StudioInput type="number" defaultValue="60" id="upload-extend-continue-at" placeholder="60" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Model</label>
                    <Select defaultValue="V4" onValueChange={v => { document.getElementById('upload-extend-model-val').value = v; }}>
                      <SelectTrigger className="w-full" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="V4">V4</SelectItem>
                        <SelectItem value="V4_5">V4.5</SelectItem>
                        <SelectItem value="V4_5PLUS">V4.5+</SelectItem>
                        <SelectItem value="V5">V5</SelectItem>
                        <SelectItem value="V5_5">V5.5</SelectItem>
                      </SelectContent>
                    </Select>
                    <input type="hidden" id="upload-extend-model-val" defaultValue="V4" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Style Tags</label>
                  <StudioInput placeholder="Hip-Hop, Trap, Cinematic..." id="upload-extend-style" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Title</label>
                  <StudioInput placeholder="Extended Track" id="upload-extend-title" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Prompt / Lyrics</label>
                  <StudioTextarea
                    placeholder="Continue the song naturally..."
                    id="upload-extend-prompt"
                    rows={3}
                  />
                </div>
                <Button
                  onClick={async () => {
                    const audioUrl = document.getElementById('upload-extend-url')?.value?.trim();
                    if (!audioUrl) { toast.error('Please provide an audio URL or select from library'); return; }
                    try {
                      setIsGenerating(true);
                      const res = await axios.post(`${API}/ai/upload-extend`, {
                        audio_url: audioUrl,
                        continue_at: parseInt(document.getElementById('upload-extend-continue-at')?.value || '60'),
                        style: document.getElementById('upload-extend-style')?.value?.trim() || undefined,
                        title: document.getElementById('upload-extend-title')?.value?.trim() || undefined,
                        prompt: document.getElementById('upload-extend-prompt')?.value?.trim() || undefined,
                        model_version: document.getElementById('upload-extend-model-val')?.value || 'V4',
                      }, { headers: { Authorization: `Bearer ${token}` } });
                      toast.success(res.data.message || 'Upload extend started!');
                      refreshTasks();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Upload extend failed');
                    } finally {
                      setIsGenerating(false);
                    }
                  }}
                  disabled={isGenerating}
                  className="w-full"
                  style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})` }}
                >
                  {isGenerating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FastForward className="w-4 h-4 mr-2" />}
                  Upload & Extend
                </Button>
              </div>
            </GlassCard>
          </TabsContent>

          {/* Cover Art Tab */}
          <TabsContent value="cover_image">
            <GlassCard>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: theme.primary }}>
                <ImagePlus className="w-5 h-5" />
                AI Cover Art Generator
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                Generate personalized cover art for your AI-generated music. Select a completed song to create 2 unique cover images.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Song Task ID</label>
                  <div className="flex gap-2">
                    <StudioInput
                      placeholder="Enter task ID of a completed song..."
                      value={coverImageTaskId}
                      onChange={(e) => setCoverImageTaskId(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setSongPickerTarget('cover_image'); setShowSongPicker(true); }}
                    >
                      Pick Song
                    </Button>
                  </div>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Use "Pick Song" to select from your completed AI tracks, or paste a task ID directly.</p>
                  {coverImageTaskId && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      <CopyIdBadge label="Task ID" value={coverImageTaskId} />
                    </div>
                  )}
                </div>
                <Button
                  onClick={async () => {
                    if (!coverImageTaskId?.trim()) { toast.error('Please select a song or enter a task ID'); return; }
                    const taskId = coverImageTaskId.trim();
                    try {
                      setIsGenerating(true);
                      const res = await axios.post(`${API}/ai/cover-image`, { task_id: taskId }, { headers: { Authorization: `Bearer ${token}` } });
                      if (res.data.task_id) {
                        toast.success(res.data.message || 'Cover art generation started!');
                        setCoverImageResult(null);
                        refreshTasks();
                      } else {
                        toast.error('Failed to start cover art generation');
                      }
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Cover art generation failed');
                    } finally {
                      setIsGenerating(false);
                    }
                  }}
                  disabled={isGenerating}
                  className="w-full"
                  style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})` }}
                >
                  {isGenerating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ImagePlus className="w-4 h-4 mr-2" />}
                  Generate Cover Art
                </Button>
                {coverImageResult && (
                  <div className="mt-4 text-center">
                    <img src={coverImageResult} alt="Generated cover" className="mx-auto rounded-lg max-w-xs shadow-lg" />
                    <div className="mt-2 flex gap-2 justify-center">
                      <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(coverImageResult); toast.success('Image URL copied!'); }}>
                        Copy URL
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => window.open(coverImageResult, '_blank')}>
                        <Download className="w-4 h-4 mr-1" /> Download
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </GlassCard>
          </TabsContent>

          <TabsContent value="trash">
            <GlassCard>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold flex items-center gap-2" style={{ color: theme.primary }}>
                  <Trash2 className="w-5 h-5" />
                  Trash
                  <span className="text-sm font-normal" style={{ color: 'var(--text-muted)' }}>({trashItems.length} items)</span>
                </h2>
                {trashItems.length > 0 && (
                  <Button
                    onClick={emptyTrash}
                    size="sm"
                    variant="outline"
                    className="text-red-400 border-red-400/30 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Empty Trash
                  </Button>
                )}
              </div>

              <ScrollArea className="h-[500px]">
                {trashItems.length > 0 ? (
                  <div className="space-y-2">
                    {trashItems.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition group"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          {item.album_cover || item.cover_image ? (
                            <img src={item.album_cover || item.cover_image} alt={item.title} className="w-10 h-10 rounded object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                              <Music className="w-4 h-4 text-gray-600" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="font-medium text-sm truncate">{item.title || 'Untitled'}</div>
                            <div className="text-xs flex items-center gap-2" style={{ color: 'var(--text-muted)' }}>
                              <span className="px-1.5 py-0.5 rounded text-xs" style={{ backgroundColor: item.source === 'ai' ? 'rgba(139,92,246,0.2)' : 'rgba(0,240,255,0.2)', color: item.source === 'ai' ? '#a78bfa' : '#00f0ff' }}>
                                {item.source === 'ai' ? 'AI Library' : 'Music'}
                              </span>
                              {item.artist && <span>{item.artist}</span>}
                              {item.deleted_at && <span>Deleted {new Date(item.deleted_at).toLocaleDateString()}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => restoreFromTrash(item.id)}
                            className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                            title="Restore"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => permanentDelete(item.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            title="Delete Permanently"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>
                    <Trash2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Trash is empty</p>
                    <p className="text-xs mt-1">Deleted songs will appear here</p>
                  </div>
                )}
              </ScrollArea>
            </GlassCard>
          </TabsContent>

        </Tabs>

        {result && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-4">
            <GlassCard className="border-2" style={{ borderColor: theme.primary }}>
              <div className="flex items-start gap-3">
                <Sparkles className="w-6 h-6 flex-shrink-0" style={{ color: theme.primary }} />
                <div>
                  <h3 className="font-bold" style={{ color: theme.primary }}>Task Submitted</h3>
                  <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{result.message}</p>
                  <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>Task ID: {result.taskId}</p>
                </div>
              </div>
            </GlassCard>
          </motion.div>
        )}

        </div>
      </main>

      {/* OUTPUT RESIZE HANDLE */}
      <div
        className="hidden lg:flex w-1.5 flex-shrink-0 cursor-col-resize items-center justify-center group hover:bg-white/5 transition"
        onMouseDown={(e) => handleResizeStart('output', e)}
        style={{ backgroundColor: 'transparent' }}
      >
        <div className="w-0.5 h-8 rounded-full bg-white/10 group-hover:bg-white/30 transition" />
      </div>

      {/* RIGHT PANEL - OUTPUT HISTORY */}
      <aside
        className="hidden lg:flex flex-col flex-shrink-0 h-full overflow-hidden"
        style={{ backgroundColor: 'var(--bg-card)', borderLeft: '1px solid var(--border-color)', width: `${outputWidth}px` }}
      >
        <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border-color)' }}>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" style={{ color: theme.primary }} />
            <h2 className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>Output</h2>
            {tasks.length > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${theme.primary}20`, color: theme.primary }}>{tasks.length}</span>
            )}
          </div>
          <Button
            onClick={() => { fetchTasks(); fetchSongLibraries(); }}
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            style={{ color: 'var(--text-muted)' }}
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
          {allProcessingTasks.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-widest px-1" style={{ color: 'var(--text-muted)' }}>Processing</p>
              {allProcessingTasks.map((task) => {
                const taskId = task.suno_task_id || task.task_id || task.id;
                const title = task.title || task.prompt || getTaskLabel(task);
                const progress = task.progress || 0;
                let taskSettings = {};
                try { taskSettings = task.settings ? (typeof task.settings === 'string' ? JSON.parse(task.settings) : task.settings) : {}; } catch {}
                const stage = progress >= 100 ? 'complete' : taskSettings.callback_stage || (progress >= 65 ? 'first' : progress >= 25 ? 'text' : 'queued');
                const stages = [
                  { key: 'queued', label: 'Queued', pct: 0 },
                  { key: 'text', label: 'Text Ready', pct: 25 },
                  { key: 'first', label: '1st Track', pct: 65 },
                  { key: 'complete', label: 'Done', pct: 100 },
                ];
                const currentIdx = Math.max(0, stages.findIndex(s => s.key === stage));
                return (
                  <div key={taskId} className="rounded-lg p-3" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1 rounded" style={{ backgroundColor: `${getTaskColor(task)}20`, color: getTaskColor(task) }}>{getTaskIcon(task)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>{title}</p>
                        <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{getTaskLabel(task)}</p>
                      </div>
                      <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" style={{ color: theme.primary }} />
                    </div>
                    <div className="flex items-center gap-0.5 my-2">
                      {stages.map((s, i) => {
                        const isDone = i < currentIdx;
                        const isActive = i === currentIdx;
                        return (
                          <React.Fragment key={s.key}>
                            <div className="flex flex-col items-center" style={{ flex: '0 0 auto' }}>
                              <motion.div
                                className="rounded-full flex items-center justify-center"
                                style={{
                                  width: 16, height: 16,
                                  backgroundColor: isDone ? theme.primary : isActive ? `${theme.primary}40` : 'var(--bg-primary)',
                                  border: isActive ? `2px solid ${theme.primary}` : isDone ? 'none' : '1px solid var(--border-color)',
                                }}
                                animate={isActive ? { scale: [1, 1.2, 1] } : {}}
                                transition={isActive ? { duration: 1.5, repeat: Infinity } : {}}
                              >
                                {isDone && <Check className="w-2.5 h-2.5 text-black" />}
                                {isActive && <motion.div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: theme.primary }} animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1.2, repeat: Infinity }} />}
                              </motion.div>
                              <span className="text-[8px] mt-0.5 whitespace-nowrap" style={{ color: isDone ? theme.primary : isActive ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: isActive ? 600 : 400 }}>{s.label}</span>
                            </div>
                            {i < stages.length - 1 && (
                              <div className="flex-1 h-0.5 mx-0.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--bg-primary)', minWidth: 8 }}>
                                <motion.div className="h-full rounded-full" style={{ backgroundColor: theme.primary }} initial={{ width: 0 }} animate={{ width: isDone ? '100%' : isActive ? '50%' : '0%' }} transition={{ duration: 0.6 }} />
                              </div>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                    <div className="w-full h-1 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--bg-primary)' }}>
                      <motion.div
                        className="h-full rounded-full"
                        style={{ backgroundColor: theme.primary }}
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.max(progress, 5)}%` }}
                        transition={{ duration: 0.8, ease: 'easeOut' }}
                      />
                    </div>
                    <p className="text-[9px] mt-1 flex justify-between" style={{ color: 'var(--text-muted)' }}>
                      <span>{taskSettings.stage_message || (progress === 0 ? 'Waiting to start...' : 'Processing...')}</span>
                      <span>{progress}%</span>
                    </p>
                  </div>
                );
              })}
            </div>
          )}

          {allCompletedTasks.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-widest px-1" style={{ color: 'var(--text-muted)' }}>Completed</p>
              {allCompletedTasks.slice(0, 20).map((task) => {
                const taskId = task.suno_task_id || task.task_id || task.id;
                const title = task.title || task.prompt || getTaskLabel(task);
                const audioUrl = typeof task.audio_url === 'string' && task.audio_url.startsWith('{')
                  ? (() => { try { const p = JSON.parse(task.audio_url); return p.instrumental || p.vocals || Object.values(p)[0]; } catch { return task.audio_url; } })()
                  : task.audio_url;
                return (
                  <div key={taskId} className="rounded-lg p-3 group" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1 rounded" style={{ backgroundColor: `${getTaskColor(task)}20`, color: getTaskColor(task) }}>{getTaskIcon(task)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>{title}</p>
                        <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{getTaskLabel(task)}</p>
                      </div>
                      <Check className="w-4 h-4 flex-shrink-0 text-green-400" />
                    </div>
                    {audioUrl && (
                      <div className="flex items-center gap-1 mt-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs"
                          onClick={() => {
                            const audio = new Audio(audioUrl);
                            audio.play().catch(() => {});
                          }}
                          style={{ color: theme.primary }}
                        >
                          <Play className="w-3 h-3 mr-1" /> Play
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs"
                          onClick={() => {
                            const fname = (title || 'download').replace(/[^a-zA-Z0-9_\- ]/g, '') + '.mp3';
                            import('../utils/downloadFile').then(m => m.downloadFile(audioUrl, fname));
                          }}
                          style={{ color: 'var(--text-secondary)' }}
                        >
                          <Download className="w-3 h-3 mr-1" /> Save
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs"
                          onClick={() => handleSaveTaskToLibrary(task)}
                          style={{ color: 'var(--text-secondary)' }}
                        >
                          <FolderPlus className="w-3 h-3 mr-1" /> Library
                        </Button>
                      </div>
                    )}
                    <div className="flex items-center gap-1 mt-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-1.5 text-[10px]"
                        onClick={() => { navigator.clipboard.writeText(taskId); toast.success('Task ID copied'); }}
                        style={{ color: 'var(--text-muted)' }}
                      >
                        <Copy className="w-3 h-3 mr-1" /> ID
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-1.5 text-[10px] ml-auto"
                        onClick={() => handleDismissTask(task)}
                        style={{ color: 'var(--text-muted)' }}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {allFailedTasks.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-widest px-1 text-red-400">Failed</p>
              {allFailedTasks.map((task) => {
                const taskId = task.suno_task_id || task.task_id || task.id;
                const title = task.title || task.prompt || getTaskLabel(task);
                return (
                  <div key={taskId} className="rounded-lg p-3" style={{ backgroundColor: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.2)' }}>
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 text-red-400" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>{title}</p>
                        <p className="text-[10px] text-red-400">{task.error || 'Task failed'}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        onClick={() => handleDismissTask(task)}
                        style={{ color: 'var(--text-muted)' }}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {tasks.length === 0 && (
            <div className="text-center py-12">
              <Clock className="w-10 h-10 mx-auto mb-3 opacity-20" style={{ color: 'var(--text-muted)' }} />
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No tasks yet</p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Generate something to see results here</p>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
};

export default AIStudio;
